import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Card, Table, Alert, Badge, ListGroup, ProgressBar, Form, Nav, Container, Accordion } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, ResponsiveContainer } from 'recharts';
import { fetchGoodRuns, updateReferenceRun, fetchProject, fetchProjectProcessParameters, fetchProjectQualityAttributes, fetchUnselectedOutputParameters, fetchGoodRunsForReference, fetchCurrentReferenceRuns, updateReferenceRunSelections } from '../services/api';
import './KpiCalculationModal.css';

const KpiCalculationModal = ({ show, onHide, worksheets, projectId }) => {
  // Multi-step wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [stepData, setStepData] = useState({
    step1: { mappedColumns: { metadata: [], kpi: [], input: [] } },
    step2: { goodColumn: '', goodValue: '', goodRuns: [], badRuns: [] },
    step3: { scalingComplete: false, medianMadData: null },
    step4: { selectedReferenceRuns: [], referenceProfile: null },
    step5: { weights: null, alpha: 0, auroc: 0 },
    step6: { ewmaLambda: 0.2, alarmList: [], anomalyThreshold: 3 },
    step7: { eventLogData: [], instrumentFloors: {}, severityFilters: {} },
    step8: { liftMatrix: [], selectedCell: null },
    step9: { selectedInput: '', selectedKpi: '', maxLag: 3, lagResults: null },
    step10: { rootCauseData: [], editableActions: {} },
    step11: { auditConfig: null, auditHistory: [], archivedVersions: 0 }
  });

  // Step 1 - Column mapping state
  const [availableColumns, setAvailableColumns] = useState([]);
  const [mappedColumns, setMappedColumns] = useState({
    metadata: [],
    kpi: [],
    input: []
  });
  const [previewData, setPreviewData] = useState([]);
  const [selectedWorksheet, setSelectedWorksheet] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);

  // Step 1 - New redesigned state
  const [projectData, setProjectData] = useState(null);
  const [inputParameters, setInputParameters] = useState([]);
  const [kpiParameters, setKpiParameters] = useState([]);
  const [originalKpiParameters, setOriginalKpiParameters] = useState([]); // Track original KPIs from project
  const [userAddedKpiIds, setUserAddedKpiIds] = useState(new Set()); // Track user-added KPI IDs
  const [allOutputParameters, setAllOutputParameters] = useState([]);
  const [availableKpis, setAvailableKpis] = useState([]);
  const [showAddKpiModal, setShowAddKpiModal] = useState(false);
  const [selectedKpisForAdding, setSelectedKpisForAdding] = useState(new Set()); // For multi-selection in modal
  const [loadingProjectData, setLoadingProjectData] = useState(false);

  // Step 2 - Good/Bad runs state
  const [goodColumn, setGoodColumn] = useState('');
  const [goodValue, setGoodValue] = useState('1');
  const [goodBadCounts, setGoodBadCounts] = useState({ good: 0, bad: 0 });
  
  // Step 2 - Reference run selection state
  const [availableGoodRuns, setAvailableGoodRuns] = useState([]); // Good runs available for selection
  const [currentReferenceRuns, setCurrentReferenceRuns] = useState([]); // Current reference runs
  const [selectedReferenceRunIds, setSelectedReferenceRunIds] = useState(new Set()); // Selected for new reference
  const [loadingReferenceRuns, setLoadingReferenceRuns] = useState(false);
  const [savingReferenceRuns, setSavingReferenceRuns] = useState(false);

  // Step 3 - Scaling state
  const [showDebugTable, setShowDebugTable] = useState(false);
  const [medianMadData, setMedianMadData] = useState([]);
  const [scaledData, setScaledData] = useState([]);
  const [showScaledDataTable, setShowScaledDataTable] = useState(true);

  // Step 4 - Reference profile state
  const [selectedReferenceRuns, setSelectedReferenceRuns] = useState([]);
  const [referenceChartData, setReferenceChartData] = useState([]);

  // Step 5 - KPI weighting state
  const [weightingData, setWeightingData] = useState([]);
  const [blendingResults, setBlendingResults] = useState({ alpha: 0, auroc: 0 });

  // Step 6 - Adaptive Anomaly Threshold (EWMA) state
  const [ewmaLambda, setEwmaLambda] = useState(0.2);
  const [anomalyData, setAnomalyData] = useState([]);
  const [alarmList, setAlarmList] = useState([]);

  // Step 7 - Event Logging state
  const [eventLogData, setEventLogData] = useState([]);
  const [eventFilters, setEventFilters] = useState({ run: '', variable: '', severity: '' });
  const [instrumentFloors, setInstrumentFloors] = useState({
    pH: 0.01,
    DO: 0.1,
    Temperature: 0.1,
    Agitation: 1.0
  });

  // Step 8 - KPI-Event Lift Matrix state
  const [liftMatrix, setLiftMatrix] = useState([]);
  const [selectedMatrixCell, setSelectedMatrixCell] = useState(null);

  // Step 9 - Time-Lag Analysis state
  const [selectedInput, setSelectedInput] = useState('');
  const [selectedKpi, setSelectedKpi] = useState('');
  const [maxLag, setMaxLag] = useState(3);
  const [lagAnalysisData, setLagAnalysisData] = useState({ correlation: [], pvalue: [] });

  // Step 10 - Root-Cause Scoring state
  const [rootCauseData, setRootCauseData] = useState([]);
  const [editableActions, setEditableActions] = useState({});

  // Step 11 - Lifecycle & Traceability state
  const [auditConfig, setAuditConfig] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditFilter, setAuditFilter] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);

  const steps = [
    { id: 1, title: 'Column Mapping', icon: '🗂️' },
    { id: 2, title: 'Reference Run Selection', icon: '⭐' },
    { id: 3, title: 'Scale KPIs', icon: '📏' },
    { id: 4, title: 'Reference Profile', icon: '📈' },
    { id: 5, title: 'KPI Weighting', icon: '⚖️' },
    { id: 6, title: 'Anomaly Detection', icon: '🚨' },
    { id: 7, title: 'Event Logging', icon: '📝' },
    { id: 8, title: 'Lift Matrix', icon: '🔥' },
    { id: 9, title: 'Time-Lag Analysis', icon: '⏰' },
    { id: 10, title: 'Root-Cause Scoring', icon: '🎯' },
    { id: 11, title: 'Lifecycle & Traceability', icon: '📋' }
  ];

  useEffect(() => {
    if (worksheets && worksheets.length > 0) {
      const firstWorksheet = worksheets[0];
      setSelectedWorksheet(firstWorksheet);
      
      if (firstWorksheet.data && firstWorksheet.data.length > 0) {
        const headers = Object.keys(firstWorksheet.data[0]);
        setAvailableColumns(headers.map((header, index) => ({
          id: `col-${index}`,
          name: header,
          type: inferColumnType(firstWorksheet.data, header)
        })));
        
        setPreviewData(firstWorksheet.data.slice(0, 5));
      }
    }
  }, [worksheets]);

  // Update good/bad counts when goodColumn or goodValue changes
  useEffect(() => {
    if (goodColumn && selectedWorksheet?.data) {
      console.log('useEffect triggered for good/bad split');
      processGoodBadSplit();
    }
  }, [goodColumn, goodValue, selectedWorksheet]);

  // Load reference runs data when modal opens and projectId is available
  useEffect(() => {
    if (show && projectId && currentStep === 2) {
      loadReferenceRunsData();
    }
  }, [show, projectId, currentStep]);

  // Load project data and parameters when modal opens
  useEffect(() => {
    if (show && projectId) {
      fetchProjectData();
    }
  }, [show, projectId]);

  const loadReferenceRunsData = async () => {
    if (!projectId) return;
    
    setLoadingReferenceRuns(true);
    try {
      // Load good runs available for selection and current reference runs
      const [goodRuns, referenceRuns] = await Promise.all([
        fetchGoodRunsForReference(projectId),
        fetchCurrentReferenceRuns(projectId)
      ]);
      
      setAvailableGoodRuns(goodRuns);
      setCurrentReferenceRuns(referenceRuns);
      
      // Pre-select current reference runs
      const currentReferenceIds = new Set(referenceRuns.map(run => run.runId));
      setSelectedReferenceRunIds(currentReferenceIds);
      
    } catch (error) {
      console.error('Error loading reference runs data:', error);
    } finally {
      setLoadingReferenceRuns(false);
    }
  };

  const toggleReferenceRunSelection = (runId) => {
    setSelectedReferenceRunIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(runId)) {
        newSet.delete(runId);
      } else {
        newSet.add(runId);
      }
      return newSet;
    });
  };

  const saveReferenceRunSelections = async () => {
    if (!projectId) {
      alert('No project ID available. Please ensure a project is selected.');
      return;
    }
    
    if (selectedReferenceRunIds.size === 0) {
      alert('Please select at least one Reference Run to proceed.');
      return;
    }
    
    console.log('Saving reference run selections:', {
      projectId,
      selectedRuns: Array.from(selectedReferenceRunIds),
      size: selectedReferenceRunIds.size
    });
    
    setSavingReferenceRuns(true);
    try {
      // Update reference run selections in the backend
      const result = await updateReferenceRunSelections(projectId, Array.from(selectedReferenceRunIds));
      console.log('Save result:', result);
      
      // Show success message immediately
      alert(`Successfully updated ${selectedReferenceRunIds.size} Reference Run(s).`);
      
      // Try to refresh the data, but don't fail if this doesn't work
      try {
        await loadReferenceRunsData();
      } catch (refreshError) {
        console.warn('Warning: Could not refresh reference runs data after save:', refreshError);
        // Don't show error to user since the save was successful
      }
      
    } catch (error) {
      console.error('Error saving reference run selections:', error);
      alert(`Failed to save Reference Run selections: ${error.message || 'Unknown error'}. Please try again.`);
    } finally {
      setSavingReferenceRuns(false);
    }
  };

  const inferColumnType = (data, columnName) => {
    const sampleValues = data.slice(0, 5).map(row => row[columnName]).filter(val => val != null);
    if (sampleValues.length === 0) return 'text';
    
    const firstValue = sampleValues[0];
    if (!isNaN(firstValue) && !isNaN(parseFloat(firstValue))) {
      return 'number';
    }
    if (Date.parse(firstValue)) {
      return 'date';
    }
    return 'text';
  };

  // Step 1 - New data fetching functions
  const fetchProjectData = async () => {
    if (!projectId) return;
    
    setLoadingProjectData(true);
    try {
      console.log('🔄 Fetching project-specific data for project:', projectId);
      
      // Fetch project details
      const project = await fetchProject(projectId);
      setProjectData(project);
      
      // Fetch project-specific process parameters and quality attributes
      const [processParams, qualityAttrs] = await Promise.all([
        fetchProjectProcessParameters(projectId),
        fetchProjectQualityAttributes(projectId)
      ]);
      
      console.log('📊 Fetched project-specific process parameters:', processParams.length);
      console.log('📊 Fetched project-specific quality attributes:', qualityAttrs.length);
      
      // Filter selected input parameters (Input process parameters)
      const inputParams = processParams.filter(param => 
        param.processParameterType === 'Input Process Parameter'
      );
      setInputParameters(inputParams);
      
      // Filter selected output parameters (Output process parameters + Quality attributes)
      const outputParams = processParams.filter(param => 
        param.processParameterType === 'Output Process Parameter'
      );
      
      // Combine output process parameters and quality attributes
      const allSelectedOutputs = [
        ...outputParams.map(param => ({ ...param, type: 'Output Parameter' })),
        ...qualityAttrs.map(attr => ({ ...attr, type: 'Quality Attribute' }))
      ];
      setAllOutputParameters(allSelectedOutputs);
      
      // Set all selected outputs as KPIs initially
      setKpiParameters(allSelectedOutputs);
      setOriginalKpiParameters(allSelectedOutputs); // Track original KPIs from project
      setUserAddedKpiIds(new Set()); // Reset user-added KPIs
      
      // Fetch unselected output parameters for "Add KPI" modal
      const unselectedOutputs = await fetchUnselectedOutputParameters(projectId);
      setAvailableKpis(unselectedOutputs);
      
      // Update mapped columns for legacy compatibility with other steps
      setMappedColumns(prev => ({
        ...prev,
        input: inputParams.map(param => ({ id: param.id, name: param.name, type: 'input' })),
        kpi: allSelectedOutputs.map(param => ({ id: param.id, name: param.name, type: 'kpi' }))
      }));
      
      console.log('✅ Successfully loaded project-specific data');
      console.log('📈 Input Parameters:', inputParams.length);
      console.log('📊 KPI Parameters:', allSelectedOutputs.length);
      console.log('🎯 Available to Add:', unselectedOutputs.length);
      
    } catch (error) {
      console.error('❌ Error fetching project data:', error);
      
      // Check if it's a Unicode encoding error
      if (error.message && (
        error.message.includes('charmap') || 
        error.message.includes('codec') || 
        error.message.includes('Character encoding error')
      )) {
        alert('Character encoding error: Some parameter names contain special characters (like ₂, ₃) that cannot be displayed properly. The data has been automatically converted to use standard characters.');
        // Try to fetch again after backend sanitization
        setTimeout(() => fetchProjectData(), 1000);
      } else {
        alert('Failed to load project data. Please try again.');
      }
    } finally {
      setLoadingProjectData(false);
    }
  };

  const addKpiParameter = (param) => {
    // Add single parameter (for backward compatibility)
    setKpiParameters(prev => [...prev, param]);
    setAvailableKpis(prev => prev.filter(p => p.id !== param.id));
    setUserAddedKpiIds(prev => new Set([...prev, param.id])); // Track as user-added
    setShowAddKpiModal(false);
  };

  const addSelectedKpiParameters = () => {
    // Add multiple selected parameters
    const paramsToAdd = availableKpis.filter(param => selectedKpisForAdding.has(param.id));
    
    if (paramsToAdd.length === 0) {
      alert('Please select at least one KPI parameter to add.');
      return;
    }
    
    // Update state
    setKpiParameters(prev => [...prev, ...paramsToAdd]);
    setAvailableKpis(prev => prev.filter(param => !selectedKpisForAdding.has(param.id)));
    setUserAddedKpiIds(prev => {
      const newSet = new Set(prev);
      paramsToAdd.forEach(param => newSet.add(param.id));
      return newSet;
    });
    
    // Reset and close modal
    setSelectedKpisForAdding(new Set());
    setShowAddKpiModal(false);
  };

  const toggleKpiForAdding = (paramId) => {
    setSelectedKpisForAdding(prev => {
      const newSet = new Set(prev);
      if (newSet.has(paramId)) {
        newSet.delete(paramId);
      } else {
        newSet.add(paramId);
      }
      return newSet;
    });
  };

  const removeKpiParameter = (paramId) => {
    // Only allow removal of user-added KPIs
    if (!userAddedKpiIds.has(paramId)) {
      alert('Cannot remove original project KPIs. Only user-added KPIs can be removed.');
      return;
    }
    
    // Move parameter from selected back to available
    const paramToRemove = kpiParameters.find(p => p.id === paramId);
    if (paramToRemove) {
      setKpiParameters(prev => prev.filter(p => p.id !== paramId));
      setAvailableKpis(prev => [...prev, paramToRemove]);
      setUserAddedKpiIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(paramId);
        return newSet;
      });
    }
  };

  // Update mapped columns for backward compatibility with other steps
  useEffect(() => {
    console.log('🔄 Updating mappedColumns with kpiParameters:', kpiParameters);
    const newMappedColumns = {
      metadata: projectData ? [
        { id: 'project-name', name: 'Project Name', type: 'text' },
        { id: 'project-desc', name: 'Project Description', type: 'text' },
        { id: 'worksheet', name: 'Worksheet', type: 'text' },
        { id: 'created-date', name: 'Created Date', type: 'date' }
      ] : [],
      input: inputParameters.map(param => ({
        id: param.id,
        name: param.name,
        type: 'number'
      })),
      kpi: kpiParameters.map(param => ({
        id: param.id,
        name: param.name,
        type: 'number'
      }))
    };
    
    console.log('📊 New mapped KPI columns:', newMappedColumns.kpi);
    setMappedColumns(newMappedColumns);
  }, [projectData, inputParameters, kpiParameters]);

  // Navigation functions
  const goToStep = (step) => {
    if (step >= 1 && step <= 11) {
      setCurrentStep(step);
    }
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      saveCurrentStepData();
      if (currentStep < 11) {
        setCurrentStep(currentStep + 1);
        processStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (kpiParameters.length === 0) {
          alert('Please select at least one KPI parameter before proceeding.');
          return false;
        }
        if (inputParameters.length === 0) {
          alert('No input parameters found. Please ensure your project has input parameters configured.');
          return false;
        }
        return true;
      case 2:
        if (currentReferenceRuns.length === 0 && selectedReferenceRunIds.size === 0) {
          alert('Please select at least one Reference Run before proceeding.');
          return false;
        }
        return true;
      case 3:
        return true; // No validation needed for scaling step
      case 4:
        if (selectedReferenceRunIds.size === 0 && currentReferenceRuns.length === 0) {
          alert('Please select at least one reference run.');
          return false;
        }
        return true;
      case 5:
        return true;
      case 6:
        return true; // Anomaly detection step
      case 7:
        return true; // Event logging step
      case 8:
        return true; // Lift matrix step
      case 9:
        return true; // Time-lag analysis step
      case 10:
        return true; // Root cause scoring step
      case 11:
        return true; // Final step - lifecycle & traceability
      default:
        return true;
    }
  };

  const canProceedToNextStep = () => {
    let canProceed = false;
    
    switch (currentStep) {
      case 1:
        canProceed = mappedColumns.kpi.length > 0;
        break;
      case 2:
        // Check if at least one reference run is selected
        canProceed = selectedReferenceRunIds.size > 0;
        console.log(`Step 2 validation:`, {
          selectedReferenceRunIds: selectedReferenceRunIds.size,
          availableGoodRuns: availableGoodRuns.length,
          canProceed
        });
        break;
      case 3:
        canProceed = true; // Scaling step can always proceed
        break;
      case 4:
        canProceed = selectedReferenceRunIds.size > 0 || currentReferenceRuns.length > 0;
        break;
      case 5:
        canProceed = true; // Can proceed to step 6
        break;
      case 6:
        canProceed = true; // Can proceed to step 7
        break;
      case 7:
        canProceed = true; // Can proceed to step 8
        break;
      case 8:
        canProceed = true; // Can proceed to step 9
        break;
      case 9:
        canProceed = true; // Can proceed to step 10
        break;
      case 10:
        canProceed = true; // Can proceed to step 11
        break;
      case 11:
        canProceed = false; // Final step
        break;
      default:
        canProceed = true;
        break;
    }
    
    console.log(`Step ${currentStep}: canProceed = ${canProceed}`, {
      mappedKpis: mappedColumns.kpi.length,
      goodColumn,
      goodBadCounts,
      selectedReferenceRunIds: selectedReferenceRunIds.size,
      currentReferenceRuns: currentReferenceRuns.length
    });
    
    return canProceed;
  };

  const saveCurrentStepData = () => {
    const newStepData = { ...stepData };
    
    switch (currentStep) {
      case 1:
        newStepData.step1.mappedColumns = { ...mappedColumns };
        break;
      case 2:
        newStepData.step2 = {
          goodColumn,
          goodValue,
          goodRuns: getGoodRuns(),
          badRuns: getBadRuns()
        };
        break;
      case 3:
        newStepData.step3 = { scalingComplete: true, medianMadData, scaledData };
        break;
      case 4:
        newStepData.step4 = { selectedReferenceRuns, referenceProfile: referenceChartData };
        break;
      case 5:
        newStepData.step5 = { weights: weightingData, ...blendingResults };
        break;
      case 6:
        newStepData.step6 = { ewmaLambda, alarmList, anomalyThreshold: 3 };
        break;
      case 7:
        newStepData.step7 = { eventLogData, instrumentFloors, severityFilters: eventFilters };
        break;
      case 8:
        newStepData.step8 = { liftMatrix, selectedCell: selectedMatrixCell };
        break;
      case 9:
        newStepData.step9 = { selectedInput, selectedKpi, maxLag, lagResults: lagAnalysisData };
        break;
      case 10:
        newStepData.step10 = { rootCauseData, editableActions };
        break;
      case 11:
        newStepData.step11 = { auditConfig, auditHistory, archivedVersions: 25 };
        break;
      default:
        // No additional data to save for other steps
        break;
    }
    
    setStepData(newStepData);
  };

  const processStep = (step) => {
    switch (step) {
      case 2:
        processGoodBadSplit();
        break;
      case 3:
        processRobustScaling();
        break;
      case 4:
        processReferenceProfile();
        break;
      case 5:
        processKpiWeighting();
        break;
      case 6:
        processAnomalyDetection();
        break;
      case 7:
        processEventLogging();
        break;
      case 8:
        processLiftMatrix();
        break;
      case 9:
        processTimeLagAnalysis();
        break;
      case 10:
        processRootCauseScoring();
        break;
      case 11:
        processLifecycleTraceability();
        break;
      default:
        // No processing needed for other steps
        break;
    }
  };

  // Step 1 functions (existing column mapping logic)
  const moveColumnToBucket = (column, targetBucket) => {
    setAvailableColumns(prev => prev.filter(col => col.id !== column.id));
    setMappedColumns(prev => ({
      metadata: prev.metadata.filter(col => col.id !== column.id),
      kpi: prev.kpi.filter(col => col.id !== column.id),
      input: prev.input.filter(col => col.id !== column.id)
    }));

    if (targetBucket === 'available') {
      setAvailableColumns(prev => [...prev, column]);
    } else {
      setMappedColumns(prev => ({
        ...prev,
        [targetBucket]: [...prev[targetBucket], column]
      }));
    }
    
    setSelectedColumn(null);
  };

  // Step 2 functions
  const processGoodBadSplit = () => {
    if (!selectedWorksheet?.data || !goodColumn) return;
    
    const goodRuns = getGoodRuns();
    const badRuns = getBadRuns();
    
    setGoodBadCounts({ good: goodRuns.length, bad: badRuns.length });
  };

  const getGoodRuns = () => {
    if (!selectedWorksheet?.data || !goodColumn) {
      console.log('getGoodRuns: Missing data or goodColumn', {
        hasData: !!selectedWorksheet?.data,
        dataLength: selectedWorksheet?.data?.length,
        goodColumn
      });
      return [];
    }
    
    // First check what values exist in the good column
    const uniqueValues = [...new Set(selectedWorksheet.data.map(row => row[goodColumn]))];
    console.log(`Values in column '${goodColumn}':`, uniqueValues);
    console.log('Looking for goodValue:', goodValue, typeof goodValue);
    
    // Filter rows that match the good value
    const goodRows = selectedWorksheet.data.filter(row => 
      String(row[goodColumn]) === String(goodValue)
    );
    
    console.log('Good rows found:', goodRows.length);
    
    // Try different Run ID column names
    const runIdColumns = ['Run_ID', 'RunID', 'run_id', 'Run ID', 'ID', 'id'];
    let runIdColumn = null;
    
    for (const col of runIdColumns) {
      if (goodRows.length > 0 && goodRows[0].hasOwnProperty(col)) {
        runIdColumn = col;
        break;
      }
    }
    
    if (!runIdColumn) {
      console.log('No Run ID column found, using row indices');
      // If no Run ID column, use row indices as run identifiers
      return goodRows.map((row, index) => `Row_${selectedWorksheet.data.indexOf(row)}`);
    }
    
    console.log('Using Run ID column:', runIdColumn);
    const runs = [...new Set(goodRows
      .map(row => row[runIdColumn])
      .filter(Boolean))];
    
    console.log('Good runs:', runs);
    return runs;
  };

  const getBadRuns = () => {
    if (!selectedWorksheet?.data || !goodColumn) return [];
    
    // Filter rows that don't match the good value
    const badRows = selectedWorksheet.data.filter(row => 
      String(row[goodColumn]) !== String(goodValue)
    );
    
    // Try different Run ID column names
    const runIdColumns = ['Run_ID', 'RunID', 'run_id', 'Run ID', 'ID', 'id'];
    let runIdColumn = null;
    
    for (const col of runIdColumns) {
      if (badRows.length > 0 && badRows[0].hasOwnProperty(col)) {
        runIdColumn = col;
        break;
      }
    }
    
    if (!runIdColumn) {
      // If no Run ID column, use row indices as run identifiers
      return badRows.map((row, index) => `Row_${selectedWorksheet.data.indexOf(row)}`);
    }
    
    const runs = [...new Set(badRows
      .map(row => row[runIdColumn])
      .filter(Boolean))];
    
    return runs;
  };

  // Debug function to analyze Google Sheets data structure
  const analyzeDataStructure = () => {
    console.log('\n🔍 === GOOGLE SHEETS DATA ANALYSIS ===');
    
    // Step 1: Analyze worksheets prop
    console.log('📊 Step 1: Worksheets Prop Analysis');
    console.log('Worksheets received:', worksheets);
    console.log('Number of worksheets:', worksheets?.length || 0);
    
    if (worksheets && worksheets.length > 0) {
      worksheets.forEach((sheet, index) => {
        console.log(`📄 Worksheet ${index + 1}:`, {
          name: sheet.name,
          hasData: !!sheet.data,
          dataLength: sheet.data?.length || 0,
          dataType: typeof sheet.data,
          firstRowKeys: sheet.data?.[0] ? Object.keys(sheet.data[0]) : 'No data'
        });
      });
    }
    
    // Step 2: Analyze selected worksheet
    console.log('\n📋 Step 2: Selected Worksheet Analysis');
    if (selectedWorksheet) {
      console.log('Selected worksheet:', {
        name: selectedWorksheet.name,
        hasData: !!selectedWorksheet.data,
        dataLength: selectedWorksheet.data?.length || 0,
        dataStructure: selectedWorksheet.data?.[0] ? 'Object' : 'Empty/Invalid'
      });
      
      if (selectedWorksheet.data && selectedWorksheet.data.length > 0) {
        console.log('📊 Available columns:', Object.keys(selectedWorksheet.data[0]));
        console.log('📝 First 3 rows sample:', selectedWorksheet.data.slice(0, 3));
        
        // Analyze potential Run ID columns
        const potentialRunIdColumns = [];
        const firstRow = selectedWorksheet.data[0];
        Object.keys(firstRow).forEach(key => {
          const value = firstRow[key];
          if (typeof value === 'string' && (
            key.toLowerCase().includes('run') ||
            key.toLowerCase().includes('id') ||
            value.toLowerCase().includes('run') ||
            value.toLowerCase().includes('r')
          )) {
            potentialRunIdColumns.push({
              column: key,
              sampleValue: value,
              type: typeof value
            });
          }
        });
        
        console.log('🏃 Potential Run ID columns:', potentialRunIdColumns);
      }
    } else {
      console.log('❌ No worksheet selected');
    }
    
    // Step 3: Analyze mapped columns
    console.log('\n📋 Step 3: Mapped Columns Analysis');
    console.log('Mapped columns:', mappedColumns);
    console.log('KPI columns:', mappedColumns.kpi?.map(col => col.name) || []);
    console.log('Metadata columns:', mappedColumns.metadata?.map(col => col.name) || []);
    console.log('Input columns:', mappedColumns.input?.map(col => col.name) || []);
    
    console.log('\n🎯 === ANALYSIS COMPLETE ===\n');
  };

  // Enhanced Step 3 functions
  const processRobustScaling = () => {
    // Enhanced debugging
    console.log('🔍 === DEBUG: processRobustScaling called ===');
    console.log('📊 selectedWorksheet:', selectedWorksheet);
    console.log('📊 selectedWorksheet.data length:', selectedWorksheet?.data?.length);
    console.log('📊 selectedWorksheet.data[0]:', selectedWorksheet?.data?.[0]);
    console.log('🎯 selectedReferenceRunIds:', selectedReferenceRunIds);
    console.log('🎯 selectedReferenceRunIds size:', selectedReferenceRunIds.size);
    console.log('🎯 currentReferenceRuns:', currentReferenceRuns);
    console.log('📈 mappedColumns:', mappedColumns);
    console.log('📈 mappedColumns.kpi:', mappedColumns.kpi);
    console.log('📈 kpiParameters (Step 1):', kpiParameters);
    console.log('📈 kpiParameters length:', kpiParameters.length);
    
    // First run the analysis
    analyzeDataStructure();
    
    // Validate that we have proper data flow from Step 1
    if (!mappedColumns || !mappedColumns.kpi || mappedColumns.kpi.length === 0) {
      console.error('❌ No KPI columns mapped in Step 1');
      console.log('🔍 Debug - mappedColumns:', mappedColumns);
      console.log('🔍 Debug - kpiParameters:', kpiParameters);
      
      // Check if we have kpiParameters but not mappedColumns.kpi
      if (kpiParameters.length > 0) {
        console.log('⚠️ Found kpiParameters but not mappedColumns.kpi - possible sync issue');
        // Try to sync manually
        const syncedKpiColumns = kpiParameters.map(param => ({
          id: param.id,
          name: param.name,
          type: 'number'
        }));
        console.log('🔧 Attempting to sync KPI columns:', syncedKpiColumns);
        
        setMappedColumns(prev => ({
          ...prev,
          kpi: syncedKpiColumns
        }));
        
        // Wait a moment for state to update then retry
        setTimeout(() => {
          console.log('🔄 Retrying processRobustScaling after sync...');
          processRobustScaling();
        }, 100);
        return;
      }
      
      alert('No KPI columns found. Please complete Step 1 (Column Mapping) first and ensure you have mapped some columns as KPIs.');
      return;
    }
    
    const kpiColumns = mappedColumns.kpi.map(col => col.name);
    console.log('📊 KPI Columns extracted from Step 1:', kpiColumns);
    
    // Validate worksheet data from Google Sheets import
    if (!selectedWorksheet || !selectedWorksheet.data || selectedWorksheet.data.length === 0) {
      console.error('❌ No worksheet data available');
      console.log('📊 selectedWorksheet structure:', selectedWorksheet);
      alert('No worksheet data available. Please go back to Step 1 and import your Google Sheets data first.');
      return;
    }
    
    console.log('📊 Worksheet data validated:', selectedWorksheet.data.length, 'rows');
    
    // Debug logging
    console.log('🔍 Step 3 Debug - Processing Robust Scaling');
    console.log('KPI Columns from Step 1:', kpiColumns);
    console.log('Selected Reference Run IDs:', Array.from(selectedReferenceRunIds));
    console.log('Current Reference Runs:', currentReferenceRuns);
    console.log('Selected Worksheet Data Sample:', selectedWorksheet?.data?.slice(0, 2));
    
    // Get reference runs from Step 2 (either newly selected or existing)
    const referenceRuns = selectedReferenceRunIds.size > 0 
      ? Array.from(selectedReferenceRunIds)
      : currentReferenceRuns.map(run => run.runId);
    
    console.log('🎯 Using Reference Runs from Step 2:', referenceRuns);
    console.log('🎯 Reference Runs length:', referenceRuns.length);
    
    if (referenceRuns.length === 0) {
      console.error('❌ No reference runs selected');
      console.log('🎯 selectedReferenceRunIds:', selectedReferenceRunIds);
      console.log('🎯 currentReferenceRuns:', currentReferenceRuns);
      alert('No reference runs selected. Please complete Step 2 (Reference Run Selection) first and select some good quality runs.');
      return;
    }
    
    // Enhanced Run ID column detection with fallback mechanisms
    const runIdColumns = ['Run_ID', 'RunID', 'run_id', 'Run ID', 'RUN_ID', 'Run', 'ID', 'Run Number', 'RunNumber'];
    let actualRunIdColumn = null;
    
    if (!selectedWorksheet.data[0]) {
      console.error('❌ No data in first row');
      alert('No data found in the worksheet. Please check your Google Sheets data.');
      return;
    }
    
    const dataColumns = Object.keys(selectedWorksheet.data[0]);
    console.log('📊 Available columns in data:', dataColumns);
    
    // Method 1: Direct column name match
    for (const col of runIdColumns) {
      if (selectedWorksheet.data[0].hasOwnProperty(col)) {
        actualRunIdColumn = col;
        console.log(`✅ Found Run ID column by name: ${col}`);
        break;
      }
    }
    
    // Method 2: If no direct match, look for columns containing 'run' or 'id'
    if (!actualRunIdColumn) {
      for (const col of dataColumns) {
        const colLower = col.toLowerCase();
        if (colLower.includes('run') || (colLower.includes('id') && !colLower.includes('project'))) {
          actualRunIdColumn = col;
          console.log(`✅ Found Run ID column by pattern: ${col}`);
          break;
        }
      }
    }
    
    // Method 3: Content analysis - check if first values look like Run IDs
    if (!actualRunIdColumn) {
      const firstRow = selectedWorksheet.data[0];
      for (const col of dataColumns) {
        const value = firstRow[col];
        if (typeof value === 'string' && (
          value.toLowerCase().includes('run') ||
          /^[a-zA-Z0-9_-]+$/.test(value) // Simple alphanumeric identifier pattern
        )) {
          actualRunIdColumn = col;
          console.log(`✅ Found Run ID column by content analysis: ${col}`);
          break;
        }
      }
    }
    
    // Method 4: Check for sequential numbering or consistent patterns
    if (!actualRunIdColumn) {
      for (const col of dataColumns) {
        const values = selectedWorksheet.data.slice(0, 5).map(row => row[col]).filter(v => v != null);
        
        // Check if values look like identifiers (not all the same, not all numbers > 1000)
        if (values.length >= 3) {
          const uniqueValues = new Set(values);
          const allNumbers = values.every(v => !isNaN(v) && isFinite(v));
          const allLarge = allNumbers && values.every(v => parseFloat(v) > 1000);
          
          if (uniqueValues.size === values.length && (!allNumbers || !allLarge)) {
            actualRunIdColumn = col;
            console.log(`✅ Found Run ID column by pattern analysis: ${col}`);
            break;
          }
        }
      }
    }
    
    // Method 5: Last resort - use first column if it has unique values
    if (!actualRunIdColumn && dataColumns.length > 0) {
      const firstCol = dataColumns[0];
      const values = selectedWorksheet.data.slice(0, 10).map(row => row[firstCol]).filter(v => v != null);
      const uniqueValues = new Set(values);
      
      if (uniqueValues.size === values.length) {
        actualRunIdColumn = firstCol;
        console.log(`⚠️ Using first column as Run ID (fallback): ${actualRunIdColumn}`);
      }
    }
    
    console.log('🏃 Run ID Column Found:', actualRunIdColumn);
    
    if (!actualRunIdColumn) {
      console.error('❌ No Run ID column found in data');
      
      // Show detailed error message with available columns
      const columnAnalysis = dataColumns.map(col => {
        const sampleValues = selectedWorksheet.data.slice(0, 3).map(row => row[col]);
        return `${col}: [${sampleValues.join(', ')}]`;
      }).join('\n');
      
      alert(`No Run ID column found in the data.

Available columns and sample values:
${columnAnalysis}

Please ensure your data has a column with unique run identifiers.

Tips:
- Column should be named something like "Run_ID", "RunID", "Run", or "ID"
- Values should be unique identifiers like "Run_001", "R1", "001", etc.
- Avoid using columns with dates, large numbers, or repeated values`);
      return;
    }
    
    // Show sample of Run IDs found
    const sampleRunIds = selectedWorksheet.data.slice(0, 5).map(row => row[actualRunIdColumn]);
    console.log('📝 Sample Run IDs from data:', sampleRunIds);
    console.log('📝 All Run IDs in data:', selectedWorksheet.data.map(row => row[actualRunIdColumn]).slice(0, 10));
    console.log('📝 Expected Reference Run IDs:', referenceRuns);
    
    // Filter reference data with proper Run ID column
    const referenceData = selectedWorksheet.data.filter(row => 
      referenceRuns.includes(row[actualRunIdColumn])
    );
    
    console.log('📋 Reference Data Found:', referenceData.length, 'rows');
    console.log('📋 Reference Data Sample:', referenceData.slice(0, 2));
    console.log('📋 Reference Data Run IDs:', referenceData.map(row => row[actualRunIdColumn]));
    
    // Debug: Show what's being compared
    console.log('🔍 Comparison Debug:');
    selectedWorksheet.data.slice(0, 10).forEach((row, idx) => {
      const runId = row[actualRunIdColumn];
      const isMatch = referenceRuns.includes(runId);
      console.log(`  Row ${idx}: ${runId} -> ${isMatch ? '✅ MATCH' : '❌ NO MATCH'}`);
    });
    console.log('📋 Reference Data Sample:', referenceData.slice(0, 2));
    
    if (referenceData.length === 0) {
      console.error('❌ No reference data found');
      const allRunIds = selectedWorksheet.data.map(row => row[actualRunIdColumn]).filter(Boolean);
      console.log('Available Run IDs in data:', allRunIds);
      alert(`No reference data found. 
      
Reference runs expected: ${referenceRuns.join(', ')}
Available Run IDs in data: ${allRunIds.slice(0, 10).join(', ')}${allRunIds.length > 10 ? '...' : ''}

Please check if the reference run IDs match the data.`);
      return;
    }

    // Calculate median and MAD for each KPI
    console.log('🔍 === Starting KPI Calculations ===');
    console.log('📊 KPI Columns to process (from Step 1):', kpiColumns);
    console.log('📊 Reference data length:', referenceData.length);
    console.log('📊 Available columns in data:', Object.keys(selectedWorksheet.data[0] || {}));
    
    // Enhanced column validation and mapping
    const dataColumnsForValidation = Object.keys(selectedWorksheet.data[0] || {});
    console.log('🔍 Data columns available:', dataColumnsForValidation);
    console.log('🔍 KPI columns from Step 1:', kpiColumns);
    
    // Smart column mapping - try to match KPI names to data columns
    const columnMapping = {};
    const validKpiColumns = [];
    const missingColumns = [];
    
    for (const kpi of kpiColumns) {
      let matchedColumn = null;
      
      // Method 1: Direct name match (case-insensitive)
      const directMatch = dataColumnsForValidation.find(col => 
        col.toLowerCase() === kpi.toLowerCase()
      );
      if (directMatch) {
        matchedColumn = directMatch;
        console.log(`✅ Direct match: "${kpi}" -> "${directMatch}"`);
      }
      
      // Method 2: Partial name match (contains keywords)
      if (!matchedColumn) {
        const kpiKeywords = kpi.toLowerCase().split(/[\s_-]+/);
        const partialMatch = dataColumnsForValidation.find(col => {
          const colLower = col.toLowerCase();
          return kpiKeywords.some(keyword => 
            keyword.length > 2 && colLower.includes(keyword)
          );
        });
        if (partialMatch) {
          matchedColumn = partialMatch;
          console.log(`✅ Partial match: "${kpi}" -> "${partialMatch}"`);
        }
      }
      
      // Method 3: For numbered columns, try to map to KPI parameter indices
      if (!matchedColumn && dataColumnsForValidation.every(col => /^\d+$/.test(col))) {
        // If all columns are numbers, try to map by index
        const kpiIndex = kpiColumns.indexOf(kpi);
        const numericColumns = dataColumnsForValidation.filter(col => /^\d+$/.test(col)).sort((a, b) => parseInt(a) - parseInt(b));
        
        if (kpiIndex < numericColumns.length) {
          matchedColumn = numericColumns[kpiIndex];
          console.log(`✅ Index-based match: "${kpi}" -> column "${matchedColumn}" (index ${kpiIndex})`);
        }
      }
      
      // Method 4: Show available options if no match found
      if (!matchedColumn) {
        console.warn(`⚠️ No match found for KPI: "${kpi}"`);
        missingColumns.push(kpi);
      } else {
        columnMapping[kpi] = matchedColumn;
        validKpiColumns.push(kpi);
      }
    }
    
    console.log('� Column Mapping Results:', columnMapping);
    console.log('✅ Valid KPI columns:', validKpiColumns);
    console.log('❌ Missing columns:', missingColumns);
    
    // If we have some valid columns, proceed with those
    if (validKpiColumns.length === 0) {
      console.error('❌ No valid KPI columns found in data');
      
      // Enhanced error message with column mapping suggestions
      const availableColumns = dataColumnsForValidation.slice(0, 20); // Show first 20 columns
      const sampleData = selectedWorksheet.data[0];
      const columnPreview = availableColumns.map(col => {
        const value = sampleData[col];
        return `${col}: ${value}`;
      }).join('\n');
      
      alert(`No KPI columns from Step 1 found in the data.

Step 1 KPI columns: ${kpiColumns.join(', ')}

Available data columns (first 20):
${columnPreview}

Possible solutions:
1. Check if your Google Sheets has proper column headers
2. Ensure the first row contains column names, not data
3. The column names should match your KPI parameter names
4. If columns are numbered (0,1,2...), we'll try to map them by position

Please verify your data import and column structure.`);
      return;
    }
    
    // Show mapping results to user
    if (missingColumns.length > 0) {
      console.warn(`⚠️ Some KPI columns couldn't be mapped: ${missingColumns.join(', ')}`);
      console.log(`✅ Successfully mapped ${validKpiColumns.length} out of ${kpiColumns.length} KPI columns`);
    }
    
    const medianMadResults = validKpiColumns.map(kpi => {
      console.log(`\n🔍 Processing KPI: ${kpi}`);
      
      // Get the actual column name from the mapping
      const actualColumnName = columnMapping[kpi];
      console.log(`🔗 KPI "${kpi}" maps to data column "${actualColumnName}"`);
      
      // Check if mapped column exists in data
      const columnExists = selectedWorksheet.data[0].hasOwnProperty(actualColumnName);
      console.log(`🔍 Mapped column '${actualColumnName}' exists:`, columnExists);
      
      if (!columnExists) {
        console.warn(`⚠️ Mapped column '${actualColumnName}' for KPI '${kpi}' not found in data`);
        console.log('📋 Available columns:', Object.keys(selectedWorksheet.data[0] || {}));
        // Return with default values
        return { kpi, median: 0.000, mad: 0.001, samples: 0 };
      }
      
      // Extract values from reference data using the mapped column name
      const rawValues = referenceData.map(row => row[actualColumnName]);
      console.log(`🔍 Raw values for '${kpi}' (column '${actualColumnName}'):`, rawValues);
      
      const values = referenceData.map(row => parseFloat(row[actualColumnName])).filter(val => !isNaN(val) && val !== null && val !== undefined);
      console.log(`🔍 Filtered values for '${kpi}':`, values);
      
      console.log(`📊 KPI: ${kpi}, Values:`, values);
      
      if (values.length === 0) {
        console.warn(`⚠️ No valid numeric values found for KPI: ${kpi}`);
        console.log(`🔍 Raw values were:`, rawValues);
        console.log(`🔍 Reference data sample for this KPI:`, referenceData.slice(0, 2).map(row => ({ runId: row[actualRunIdColumn], value: row[actualColumnName] })));
        // Return with default values
        return { kpi, median: 0.000, mad: 0.001, samples: 0 };
      }
      
      const median = calculateMedian(values);
      const mad = calculateMAD(values, median);
      
      console.log(`📈 ${kpi} - Median: ${median}, MAD: ${mad}, Samples: ${values.length}`);
      
      const result = { 
        kpi, 
        median: parseFloat(median.toFixed(3)), 
        mad: parseFloat((mad > 0 ? mad : 0.001).toFixed(3)), // Avoid division by zero
        samples: values.length 
      };
      
      console.log(`✅ Result for '${kpi}':`, result);
      return result;
    });

    console.log('🔍 === Final medianMadResults ===');
    console.log('📊 Results:', medianMadResults);
    console.log('📊 Results with actual values:', medianMadResults.filter(r => r.samples > 0));
    console.log('📊 Results with no data:', medianMadResults.filter(r => r.samples === 0));
    
    setMedianMadData(medianMadResults);
    console.log('✅ setMedianMadData called with:', medianMadResults);
    
    // Calculate scaled data for all rows using the exact formula and column mapping
    const scaledDataResults = selectedWorksheet.data.map(row => {
      const scaledRow = { 
        Run_ID: row[actualRunIdColumn],
        ...row 
      };
      
      // Apply scaling formula: (value - median) / mad using column mapping
      validKpiColumns.forEach(kpi => {
        const actualColumnName = columnMapping[kpi];
        const originalValue = parseFloat(row[actualColumnName]);
        
        if (!isNaN(originalValue) && originalValue !== null && originalValue !== undefined) {
          const medianMadInfo = medianMadResults.find(item => item.kpi === kpi);
          if (medianMadInfo && medianMadInfo.samples > 0) {
            const median = medianMadInfo.median;
            const mad = medianMadInfo.mad;
            const scaledValue = mad > 0 ? (originalValue - median) / mad : 0;
            scaledRow[`${kpi}_scaled`] = parseFloat(scaledValue.toFixed(3));
          } else {
            scaledRow[`${kpi}_scaled`] = 0.000;
          }
        } else {
          scaledRow[`${kpi}_scaled`] = 0.000;
        }
      });
      
      return scaledRow;
    });

    setScaledData(scaledDataResults);
    
    // Store the scaled KPI data for use in Step 4
    console.log('KPI Scaling completed using reference runs:', referenceRuns);
    console.log('Scaled data sample:', scaledDataResults.slice(0, 3));
  };

  const calculateMedian = (values) => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 
      ? (sorted[mid - 1] + sorted[mid]) / 2 
      : sorted[mid];
  };

  const calculateMAD = (values, median) => {
    if (values.length === 0) return 1;
    const deviations = values.map(val => Math.abs(val - median));
    return calculateMedian(deviations) || 1;
  };

  // Step 4 functions
  const processReferenceProfile = () => {
    // Get reference runs from Step 2
    const referenceRuns = selectedReferenceRunIds.size > 0 
      ? Array.from(selectedReferenceRunIds)
      : currentReferenceRuns.map(run => run.runId);
    
    // Get KPI columns (use scaled versions)
    const kpiColumns = mappedColumns.kpi.map(col => col.name);
    const scaledKpiColumns = kpiColumns.map(col => `${col}_scaled`);
    
    console.log('Building reference profile with:', {
      referenceRuns,
      kpiColumns,
      scaledKpiColumns
    });
    
    // In a real implementation, this would call the build_reference_profile function
    // For now, we'll just log that the process is complete
    console.log('Reference profile built successfully using DTW alignment and median aggregation');
    
    // Store the reference profile result for further analysis
    const referenceProfile = {
      referenceRuns,
      kpiColumns: scaledKpiColumns,
      method: 'DTW + Median',
      timestamp: new Date().toISOString()
    };
    
    // You could store this in state or send to backend
    console.log('Reference Profile Result:', referenceProfile);
  };

  // Step 5 functions
  const processKpiWeighting = () => {
    const kpiColumns = mappedColumns.kpi;
    const mockWeightingData = kpiColumns.map((kpi, index) => {
      const linearWeight = Math.random() * 0.5 + 0.1;
      const treeWeight = Math.random() * 0.5 + 0.1;
      const alpha = 0.6 + Math.random() * 0.3;
      const finalWeight = alpha * linearWeight + (1 - alpha) * treeWeight;
      
      return {
        kpi: kpi.name,
        linearWeight: linearWeight.toFixed(3),
        treeWeight: treeWeight.toFixed(3),
        finalWeight: finalWeight.toFixed(3),
        alpha: alpha.toFixed(2)
      };
    });

    setWeightingData(mockWeightingData);
    setBlendingResults({
      alpha: 0.65,
      auroc: 0.847
    });
  };

  // Step 6 functions - Adaptive Anomaly Threshold (EWMA)
  const processAnomalyDetection = () => {
    // Generate mock EWMA anomaly data
    const mockAnomalyData = [];
    const mockAlarms = [];
    
    for (let day = 0; day <= 14; day++) {
      const rawScore = Math.random() * 10 + 5;
      const ewmaScore = rawScore * ewmaLambda + (day > 0 ? mockAnomalyData[day-1]?.ewma || 5 : 5) * (1 - ewmaLambda);
      const upperBound = ewmaScore + 3 * Math.sqrt(2); // 3σ bound
      const lowerBound = ewmaScore - 3 * Math.sqrt(2);
      const isAlarm = rawScore > upperBound || rawScore < lowerBound;
      
      mockAnomalyData.push({
        day,
        rawScore: rawScore.toFixed(2),
        ewma: ewmaScore.toFixed(2),
        upperBound: upperBound.toFixed(2),
        lowerBound: lowerBound.toFixed(2),
        isAlarm
      });
      
      if (isAlarm) {
        mockAlarms.push({
          runId: `Run_${Math.floor(Math.random() * 5) + 1}`,
          day,
          severity: rawScore > upperBound ? 'HIGH' : 'LOW'
        });
      }
    }
    
    setAnomalyData(mockAnomalyData);
    setAlarmList(mockAlarms);
  };

  // Step 7 functions - Event Logging
  const processEventLogging = () => {
    const mockEventData = [];
    const variables = ['pH', 'DO', 'Temperature', 'Agitation'];
    
    for (let i = 0; i < 20; i++) {
      const severity = Math.random() * 100;
      mockEventData.push({
        id: i,
        run: `Run_${Math.floor(Math.random() * 5) + 1}`,
        day: Math.floor(Math.random() * 14) + 1,
        variable: variables[Math.floor(Math.random() * variables.length)],
        severity: severity.toFixed(1),
        severityColor: severity < 50 ? 'success' : severity < 80 ? 'warning' : 'danger',
        value: (Math.random() * 10).toFixed(2)
      });
    }
    
    setEventLogData(mockEventData);
  };

  // Step 8 functions - KPI-Event Lift Matrix
  const processLiftMatrix = () => {
    const kpiList = mappedColumns.kpi.map(col => col.name);
    const inputList = mappedColumns.input.map(col => col.name);
    
    const mockMatrix = kpiList.map(kpi => ({
      kpi,
      ...inputList.reduce((acc, input) => {
        acc[input] = {
          lift: (Math.random() * 4 + 0.5).toFixed(2),
          count: Math.floor(Math.random() * 50) + 10,
          examples: [`Day ${Math.floor(Math.random() * 14) + 1}`, `Day ${Math.floor(Math.random() * 14) + 1}`]
        };
        return acc;
      }, {})
    }));
    
    setLiftMatrix(mockMatrix);
  };

  // Step 9 functions - Time-Lag Analysis
  const processTimeLagAnalysis = () => {
    if (!selectedInput || !selectedKpi) return;
    
    const correlationData = [];
    const pvalueData = [];
    
    for (let lag = 0; lag <= maxLag; lag++) {
      const correlation = Math.random() * 0.8 - 0.4; // Random correlation between -0.4 and 0.4
      const pvalue = Math.random() * 0.1; // Random p-value between 0 and 0.1
      
      correlationData.push({ lag, value: correlation.toFixed(3) });
      pvalueData.push({ lag, value: pvalue.toFixed(4) });
    }
    
    setLagAnalysisData({ correlation: correlationData, pvalue: pvalueData });
  };

  // Step 10 functions - Root-Cause Scoring
  const processRootCauseScoring = () => {
    const mockRootCauses = [];
    const kpiList = mappedColumns.kpi.map(col => col.name);
    const inputList = mappedColumns.input.map(col => col.name);
    
    for (let i = 0; i < 10; i++) {
      const kpi = kpiList[Math.floor(Math.random() * kpiList.length)];
      const input = inputList[Math.floor(Math.random() * inputList.length)];
      const deviation = (Math.random() * 5 + 1).toFixed(2);
      const correlation = (Math.random() * 0.8 - 0.4).toFixed(3);
      const pvalue = (Math.random() * 0.05).toFixed(4);
      const lift = (Math.random() * 3 + 1).toFixed(2);
      const compositeScore = (Math.random() * 100).toFixed(1);
      
      mockRootCauses.push({
        id: i,
        kpi,
        input,
        deviation,
        correlation,
        pvalue,
        lift,
        compositeScore,
        suggestedAction: editableActions[i] || `Investigate ${input} impact on ${kpi}`
      });
    }
    
    // Sort by composite score (highest first)
    mockRootCauses.sort((a, b) => parseFloat(b.compositeScore) - parseFloat(a.compositeScore));
    setRootCauseData(mockRootCauses);
  };

  // Step 11 functions - Lifecycle & Traceability
  const processLifecycleTraceability = () => {
    // Generate comprehensive audit configuration
    const currentDate = new Date();
    const runId = `RUN_${currentDate.getFullYear()}${String(currentDate.getMonth() + 1).padStart(2, '0')}${String(currentDate.getDate()).padStart(2, '0')}_${String(currentDate.getHours()).padStart(2, '0')}${String(currentDate.getMinutes()).padStart(2, '0')}`;
    
    const auditConfigData = {
      run_id: runId,
      run_date: currentDate.toISOString().split('T')[0],
      project_id: projectId,
      worksheet_name: selectedWorksheet?.name || 'Unknown',
      reference_runs: selectedReferenceRuns,
      weight_alpha: blendingResults.alpha || 0.65,
      distance_metric: 'dtw',
      ewma_lambda: ewmaLambda,
      auroc: blendingResults.auroc || 0.93,
      threshold_rule: '3σ 14-day',
      instrument_floors: instrumentFloors,
      kpi_columns: mappedColumns.kpi.map(col => col.name),
      input_columns: mappedColumns.input.map(col => col.name),
      good_runs_count: goodBadCounts.good,
      bad_runs_count: goodBadCounts.bad,
      anomalies_detected: alarmList.length,
      events_logged: eventLogData.length,
      root_causes_identified: rootCauseData.length,
      pipeline_version: '2.1.0',
      regulatory_compliance: {
        cfr_21_part11: true,
        gmp_compliant: true,
        data_integrity: 'verified'
      }
    };

    setAuditConfig(auditConfigData);
    
    // Generate mock audit history (last 25 runs)
    const mockHistory = [];
    for (let i = 0; i < 25; i++) {
      const historyDate = new Date();
      historyDate.setDate(historyDate.getDate() - i);
      
      mockHistory.push({
        id: i,
        date: historyDate.toISOString().split('T')[0],
        runId: `RUN_${historyDate.getFullYear()}${String(historyDate.getMonth() + 1).padStart(2, '0')}${String(historyDate.getDate()).padStart(2, '0')}_${String(Math.floor(Math.random() * 24)).padStart(2, '0')}${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        referenceRuns: [`R${100 + Math.floor(Math.random() * 50)}`, `R${100 + Math.floor(Math.random() * 50)}`],
        auroc: (0.8 + Math.random() * 0.2).toFixed(3),
        distanceMetric: ['dtw', 'euclidean', 'manhattan'][Math.floor(Math.random() * 3)],
        status: i === 0 ? 'current' : 'archived'
      });
    }
    
    setAuditHistory(mockHistory);
  };

  const downloadAuditBundle = () => {
    if (!auditConfig) {
      alert('No audit configuration available. Please process the lifecycle step first.');
      return;
    }

    // Create YAML-like string representation
    const yamlContent = `# RapidCMC KPI Analysis - Audit Configuration
run_date: ${auditConfig.run_date}
run_id: ${auditConfig.run_id}
project_id: ${auditConfig.project_id}
worksheet_name: ${auditConfig.worksheet_name}

# Analysis Configuration
reference_runs: [${auditConfig.reference_runs.join(', ')}]
weight_alpha: ${auditConfig.weight_alpha}
distance_metric: ${auditConfig.distance_metric}
ewma_lambda: ${auditConfig.ewma_lambda}
auroc: ${auditConfig.auroc}
threshold_rule: "${auditConfig.threshold_rule}"

# Data Mappings
kpi_columns: [${auditConfig.kpi_columns.join(', ')}]
input_columns: [${auditConfig.input_columns.join(', ')}]

# Analysis Results
good_runs_count: ${auditConfig.good_runs_count}
bad_runs_count: ${auditConfig.bad_runs_count}
anomalies_detected: ${auditConfig.anomalies_detected}
events_logged: ${auditConfig.events_logged}
root_causes_identified: ${auditConfig.root_causes_identified}

# Instrument Floors
instrument_floors:
${Object.entries(auditConfig.instrument_floors).map(([key, value]) => `  ${key}: ${value}`).join('\n')}

# Compliance
regulatory_compliance:
  cfr_21_part11: ${auditConfig.regulatory_compliance.cfr_21_part11}
  gmp_compliant: ${auditConfig.regulatory_compliance.gmp_compliant}
  data_integrity: "${auditConfig.regulatory_compliance.data_integrity}"

# System Information
pipeline_version: ${auditConfig.pipeline_version}
generated_timestamp: ${new Date().toISOString()}
`;

    // Create and download the file
    const blob = new Blob([yamlContent], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rapidcmc_audit_${auditConfig.run_id}.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    // Also log to console for development
    console.log('Audit Configuration YAML:', yamlContent);
    console.log('Audit Bundle Downloaded:', auditConfig.run_id);
  };

  // Generate final JSON for audit logging
  const generateFinalMappingJson = () => {
    const finalMapping = {
      projectId: projectId,
      worksheetName: selectedWorksheet?.name || 'Unknown',
      timestamp: new Date().toISOString(),
      step1_columnMapping: {
        metadata: projectData ? [
          { id: 'project-name', name: 'Project Name', value: projectData.name },
          { id: 'project-desc', name: 'Project Description', value: projectData.description || 'N/A' },
          { id: 'worksheet', name: 'Worksheet', value: selectedWorksheet?.name || 'Unknown' },
          { id: 'created-date', name: 'Created Date', value: projectData.createdAt }
        ] : [],
        kpi: kpiParameters.map(param => ({
          id: param.id,
          name: param.name,
          type: param.type,
          unit: param.unit,
          category: 'kpi'
        })),
        input: inputParameters.map(param => ({
          id: param.id,
          name: param.name,
          type: 'Input Process Parameter',
          unit: param.unit,
          category: 'input'
        }))
      },
      step2_goodBadSplit: {
        goodColumn,
        goodValue,
        goodRuns: getGoodRuns(),
        badRuns: getBadRuns(),
        goodCount: goodBadCounts.good,
        badCount: goodBadCounts.bad
      },
      step3_robustScaling: {
        medianMadData,
        scaledData,
        scalingMethod: 'median_mad'
      },
      step4_referenceProfile: {
        selectedReferenceRuns,
        alignmentMethod: 'dtw',
        profileGenerated: true
      },
      step5_kpiWeighting: {
        weights: weightingData,
        blendingAlpha: blendingResults.alpha,
        auroc: blendingResults.auroc,
        method: 'hybrid_fdr_gb'
      },
      step6_anomalyDetection: {
        ewmaLambda,
        alarmsDetected: alarmList.length,
        alarmList,
        anomalyThreshold: 3
      },
      step7_eventLogging: {
        eventsLogged: eventLogData.length,
        instrumentFloors,
        eventFilters
      },
      step8_liftMatrix: {
        matrixSize: `${kpiParameters.length}x${inputParameters.length}`,
        liftMatrix,
        selectedCell: selectedMatrixCell
      },
      step9_timeLagAnalysis: {
        selectedInput,
        selectedKpi,
        maxLag,
        lagAnalysisCompleted: lagAnalysisData.correlation.length > 0
      },
      step10_rootCauseScoring: {
        rootCausesIdentified: rootCauseData.length,
        topRootCause: rootCauseData[0] || null,
        editableActions
      },
      step11_lifecycleTraceability: {
        auditConfigGenerated: !!auditConfig,
        auditRunId: auditConfig?.run_id || null,
        historyRecordsCount: auditHistory.length,
        complianceVerified: true,
        archivedVersions: 25
      },
      completedSteps: currentStep,
      totalSteps: 11
    };
    
    return finalMapping;
  };

  const handleFinish = () => {
    saveCurrentStepData();
    const finalJson = generateFinalMappingJson();
    
    console.log('Final KPI Analysis Configuration:', JSON.stringify(finalJson, null, 2));
    
    // Here you would save to audit log and proceed with analysis
    alert('KPI Analysis configuration saved! Check console for full JSON. Analysis pipeline will now begin.');
    
    onHide();
  };

  // Step Components
  const StepIndicator = () => (
    <Nav className="justify-content-center mb-4" variant="pills">
      {steps.map((step) => (
        <Nav.Item key={step.id}>
          <Nav.Link
            className={`step-indicator ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
            onClick={() => currentStep > step.id && goToStep(step.id)}
            disabled={currentStep < step.id}
          >
            <span className="step-icon">{step.icon}</span>
            <span className="step-title">{step.title}</span>
            <span className="step-number">{step.id}</span>
          </Nav.Link>
        </Nav.Item>
      ))}
    </Nav>
  );

  const ColumnItem = ({ column, onClick, isSelected, showMoveButtons = false }) => (
    <ListGroup.Item 
      className={`column-item ${isSelected ? 'selected' : ''} ${onClick ? 'clickable' : ''}`}
      onClick={() => onClick && onClick(column)}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <span className="column-name">{column.name}</span>
          <Badge bg="secondary" className="ms-2 column-type-badge">
            {column.type}
          </Badge>
        </div>
        {showMoveButtons && (
          <div className="move-buttons">
            <Button 
              size="sm" 
              variant="outline-info" 
              className="me-1"
              onClick={(e) => {
                e.stopPropagation();
                moveColumnToBucket(column, 'metadata');
              }}
            >
              📋 Meta
            </Button>
            <Button 
              size="sm" 
              variant="outline-success" 
              className="me-1"
              onClick={(e) => {
                e.stopPropagation();
                moveColumnToBucket(column, 'kpi');
              }}
            >
              📊 KPI
            </Button>
            <Button 
              size="sm" 
              variant="outline-warning"
              onClick={(e) => {
                e.stopPropagation();
                moveColumnToBucket(column, 'input');
              }}
            >
              ⚙️ Input
            </Button>
          </div>
        )}
      </div>
    </ListGroup.Item>
  );

  const BucketContainer = ({ id, title, columns, emptyMessage, bucketColor }) => (
    <Card className="mapping-bucket">
      <Card.Header className={`bucket-header bucket-${id}`}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">{title}</h6>
          <Badge bg={bucketColor} className="column-count-badge">
            {columns.length} columns
          </Badge>
        </div>
      </Card.Header>
      <Card.Body className="bucket-body">
        {columns.length === 0 ? (
          <div className="empty-bucket">
            <div className="empty-message">{emptyMessage}</div>
          </div>
        ) : (
          <ListGroup variant="flush">
            {columns.map((column) => (
              <ColumnItem 
                key={column.id} 
                column={column}
                onClick={setSelectedColumn}
                isSelected={selectedColumn?.id === column.id}
              />
            ))}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  );

  // Step 1: Column Mapping (Redesigned)
  const Step1ColumnMapping = () => (
    <Container>
      {loadingProjectData && (
        <Alert variant="info" className="mb-3">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Loading project data and parameters...
          </div>
        </Alert>
      )}

      {/* Accordion for Metadata and Input */}
      <Accordion className="mb-4" defaultActiveKey="0">
        {/* Metadata Accordion Item */}
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <span className="me-2">🏷️</span>
            <strong>Metadata</strong>
            <Badge bg="info" className="ms-2">
              {mappedColumns.metadata.length} items
            </Badge>
          </Accordion.Header>
          <Accordion.Body>
            {projectData ? (
              <Row className="g-2">
                <Col md={6}>
                  <div className="parameter-row">
                    <span className="fw-semibold me-2">Project Name:</span>
                    <span className="text-muted">{projectData.name}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="parameter-row">
                    <span className="fw-semibold me-2">Description:</span>
                    <span className="text-muted">{projectData.description || 'No description'}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="parameter-row">
                    <span className="fw-semibold me-2">Created Date:</span>
                    <span className="text-muted">{new Date(projectData.createdAt).toLocaleDateString()}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="parameter-row">
                    <span className="fw-semibold me-2">Worksheet:</span>
                    <span className="text-muted">{selectedWorksheet?.name || 'Unknown'}</span>
                  </div>
                </Col>
              </Row>
            ) : (
              <div className="text-center text-muted">
                <small>Project metadata will appear here</small>
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>

        {/* Input Accordion Item */}
        <Accordion.Item eventKey="1">
          <Accordion.Header>
            <span className="me-2">⚙️</span>
            <strong>Input Parameters</strong>
            <Badge bg="warning" className="ms-2">
              {inputParameters.length} parameters
            </Badge>
          </Accordion.Header>
          <Accordion.Body>
            {inputParameters.length === 0 ? (
              <div className="text-center text-muted">
                <small>
                  {loadingProjectData ? 'Loading input parameters...' : 'No input parameters found'}
                </small>
              </div>
            ) : (
              <Row className="g-2">
                {inputParameters.map((param) => (
                  <Col md={6} key={param.id}>
                    <div className="parameter-row">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span className="parameter-name">{param.name}</span>
                          {/* Removed param.unit display as requested */}
                        </div>
                        <Badge bg="warning" className="parameter-category-badge">
                          Input
                        </Badge>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* KPI Section - Row Layout */}
      <Card className="kpi-section-card mb-4">
        <Card.Header className="bg-success text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <span className="me-2">📊</span>
              <strong>KPI Parameters</strong>
            </h6>
            <Badge bg="light" text="dark">
              {kpiParameters.length} parameters
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          {kpiParameters.length === 0 ? (
            <div className="text-center text-muted py-4">
              <small>
                {loadingProjectData ? 'Loading KPI parameters...' : 'No KPI parameters selected'}
              </small>
            </div>
          ) : (
            <Row className="g-3">
              {kpiParameters.map((param) => (
                <Col md={6} lg={4} key={param.id}>
                  <div className="kpi-parameter-item-compact">
                    {userAddedKpiIds.has(param.id) && (
                      <Button 
                        size="sm" 
                        variant="outline-danger"
                        onClick={() => removeKpiParameter(param.id)}
                        title="Remove user-added KPI"
                        className="btn-remove-kpi"
                      >
                        ×
                      </Button>
                    )}
                    <div className="parameter-name-compact" title={param.name}>
                      {param.name}
                    </div>
                    <div className="kpi-badges-container">
                      <Badge bg="success" className="small-badge">
                        KPI
                      </Badge>
                      {!userAddedKpiIds.has(param.id) && (
                        <Badge bg="primary" className="small-badge" title="Default project KPI (cannot be removed)">
                          Default
                        </Badge>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Add KPI Button - Under KPI Row */}
      <div className="text-center mb-4">
        <Button 
          variant="outline-success" 
          size="lg"
          onClick={() => setShowAddKpiModal(true)}
          className="add-kpi-button"
        >
          <i className="fas fa-plus me-2"></i>
          Add KPI Parameter
        </Button>
      </div>

      {/* Summary Section */}
      <Card className="summary-section">
        <Card.Body>
          <div className="text-center">
            <h6 className="mb-3">📋 Configuration Summary</h6>
            <Row>
              <Col md={3}>
                <div className="text-center">
                  <Badge bg="info" className="summary-badge mb-2">Metadata</Badge>
                  <p className="mb-0 small text-muted">
                    {projectData ? '4 items configured' : 'Loading...'}
                  </p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center">
                  <Badge bg="warning" className="summary-badge mb-2">Input Parameters</Badge>
                  <p className="mb-0 small text-muted">
                    {inputParameters.length} parameters
                  </p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center">
                  <Badge bg="success" className="summary-badge mb-2">KPI Parameters</Badge>
                  <p className="mb-0 small text-muted">
                    {kpiParameters.length} parameters
                  </p>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center">
                  <Badge bg="primary" className="summary-badge mb-2">Mapped for Steps 2-3</Badge>
                  <p className="mb-0 small text-muted">
                    {mappedColumns.kpi.length} KPIs ready
                  </p>
                </div>
              </Col>
            </Row>
            
            {/* Show mapped KPI names */}
            {mappedColumns.kpi.length > 0 && (
              <div className="mt-3">
                <small className="text-muted">KPIs mapped for scaling:</small>
                <div className="mt-2">
                  {mappedColumns.kpi.map((kpi, index) => (
                    <Badge key={index} bg="outline-success" className="me-1 mb-1">
                      {kpi.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Add KPI Modal */}
      <Modal show={showAddKpiModal} onHide={() => {
        setShowAddKpiModal(false);
        setSelectedKpisForAdding(new Set()); // Reset selections when closing
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add KPI Parameters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Select multiple Output parameters and Quality Attributes to add as KPIs:
          </p>
          {availableKpis.length === 0 ? (
            <Alert variant="info">
              No additional KPI parameters available. All Output parameters and Quality Attributes are already selected.
            </Alert>
          ) : (
            <>
              <div className="mb-3">
                <small className="text-muted">
                  {selectedKpisForAdding.size} of {availableKpis.length} parameters selected
                </small>
              </div>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {availableKpis.map((param) => (
                  <div key={param.id} className="border rounded p-3 mb-2">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id={`kpi-${param.id}`}
                        checked={selectedKpisForAdding.has(param.id)}
                        onChange={() => toggleKpiForAdding(param.id)}
                      />
                      <label className="form-check-label w-100" htmlFor={`kpi-${param.id}`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span className="fw-semibold">{param.name}</span>
                            {/* Removed param.unit display as requested */}
                            <div className="small text-muted mt-1">
                              {/* Removed param.type display as requested */}
                              {param.description && `${param.description}`}
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAddKpiModal(false);
            setSelectedKpisForAdding(new Set());
          }}>
            Cancel
          </Button>
          {availableKpis.length > 0 && (
            <Button 
              variant="primary" 
              onClick={addSelectedKpiParameters}
              disabled={selectedKpisForAdding.size === 0}
            >
              Add {selectedKpisForAdding.size > 0 ? selectedKpisForAdding.size : ''} KPI{selectedKpisForAdding.size !== 1 ? 's' : ''}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );

  // Step 2: Reference Run Selection
  const Step2GoodBadSplit = () => (
    <Container>
      <Card>
        <Card.Header>
          <h5 className="mb-0">⭐ Select Reference Runs</h5>
        </Card.Header>
        <Card.Body>
          {loadingReferenceRuns ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading reference runs data...</p>
            </div>
          ) : (
            <>
              <Alert variant="info" className="mb-3">
                <div className="d-flex align-items-center">
                  <i className="fas fa-info-circle me-2"></i>
                  <div>
                    <strong>Select Reference Runs:</strong> Choose one or more runs that represent your best performance. 
                    These will be used as the reference for comparison and analysis throughout the KPI calculation process.
                  </div>
                </div>
              </Alert>

              {/* Current Reference Runs */}
              {currentReferenceRuns.length > 0 && (
                <div className="mb-4">
                  <h6 className="mb-2">
                    <Badge bg="primary" className="me-2">
                      Current Reference Runs ({currentReferenceRuns.length})
                    </Badge>
                  </h6>
                  <Row className="g-2">
                    {currentReferenceRuns.map((run) => (
                      <Col md={6} lg={4} key={run.runId}>
                        <Card className="border-primary" style={{ borderWidth: '2px' }}>
                          <Card.Body className="py-2">
                            <div className="d-flex align-items-center">
                              <i className="fas fa-star text-warning me-2"></i>
                              <div>
                                <small className="fw-bold">{run.runId}</small>
                                <br />
                                <small className="text-muted">Index: {run.runIndex}</small>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* Available Good Runs for Selection */}
              {availableGoodRuns.length === 0 && currentReferenceRuns.length === 0 ? (
                <Alert variant="warning">
                  <strong>⚠️ No Good Runs Found:</strong> No runs with "Good Run" quality were found in this project. 
                  Please ensure you have bioreactor runs marked as "Good Run" in your project.
                </Alert>
              ) : availableGoodRuns.length === 0 ? (
                <Alert variant="success">
                  <strong>✅ All Good Runs Selected:</strong> All available good runs are already marked as Reference Runs.
                </Alert>
              ) : (
                <>
                  <div className="mb-3">
                    <h6 className="mb-3">
                      <Badge bg="success" className="me-2">
                        {availableGoodRuns.length} Good Run{availableGoodRuns.length !== 1 ? 's' : ''} Available for Selection
                      </Badge>
                    </h6>
                    <small className="text-muted">
                      Select multiple runs to add as Reference Runs. Currently selected: {selectedReferenceRunIds.size}
                    </small>
                  </div>

                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {availableGoodRuns.map((run) => (
                      <div key={run.runId} className="border rounded p-3 mb-2">
                        <div className="form-check">
                          <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id={`run-${run.runId}`}
                            checked={selectedReferenceRunIds.has(run.runId)}
                            onChange={() => toggleReferenceRunSelection(run.runId)}
                          />
                          <label className="form-check-label w-100" htmlFor={`run-${run.runId}`}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <span className="fw-semibold">{run.runId}</span>
                                <Badge bg="secondary" className="ms-2">Index: {run.runIndex}</Badge>
                                <div className="small text-muted mt-1">
                                  Start Date: {new Date(run.cultureStartDateTime).toLocaleDateString()}
                                  {run.cultureEndDateTime && (
                                    <> • End Date: {new Date(run.cultureEndDateTime).toLocaleDateString()}</>
                                  )}
                                </div>
                                {run.notes && (
                                  <div className="small text-muted mt-1">
                                    Notes: {run.notes}
                                  </div>
                                )}
                              </div>
                              <div>
                                <Badge bg="success">Good Run</Badge>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Save Button */}
                  <div className="mt-4 text-center">
                    <Button 
                      variant="primary" 
                      onClick={saveReferenceRunSelections}
                      disabled={savingReferenceRuns || selectedReferenceRunIds.size === 0}
                      className="px-4"
                    >
                      {savingReferenceRuns ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Saving Reference Runs...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save {selectedReferenceRunIds.size > 0 ? selectedReferenceRunIds.size : ''} Reference Run{selectedReferenceRunIds.size !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                    {selectedReferenceRunIds.size === 0 && (
                      <div className="small text-danger mt-2">
                        Please select at least one run to proceed
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 3: Scale KPIs
  const Step3RobustScaling = () => (
    <Container>
      <Card>
        <Card.Header>
          <h5 className="mb-0">📏 Scale KPIs (Median / MAD)</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="success">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <i className="fas fa-check-circle" style={{ fontSize: '2rem', color: 'green' }}></i>
              </div>
              <div>
                <h6 className="mb-1">KPI Scaling Complete</h6>
                <p className="mb-0">
                  All KPI columns have been scaled using robust median and MAD (Median Absolute Deviation) 
                  from reference runs. This ensures outlier-resistant normalization for better analysis.
                </p>
              </div>
            </div>
          </Alert>

          <div className="mt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex gap-2">
              <Button 
                variant="primary" 
                onClick={processRobustScaling}
              >
                <i className="fas fa-calculator me-2"></i>
                Calculate Scaling Values
              </Button>
              <Button 
                variant="outline-info" 
                onClick={analyzeDataStructure}
                size="sm"
              >
                <i className="fas fa-search me-2"></i>
                Debug Data Structure
              </Button>
            </div>
            <div>
              <Form.Check 
                type="checkbox" 
                id="scaled-data-checkbox"
                label="Show Scaled Data Table"
                checked={showScaledDataTable}
                onChange={(e) => setShowScaledDataTable(e.target.checked)}
                className="mb-0"
              />
            </div>
          </div>

          <div className="mt-2">
            <Form.Check 
              type="checkbox" 
              id="debug-checkbox"
              label="Show Debug Table (Median/MAD values)"
              checked={showDebugTable}
              onChange={(e) => setShowDebugTable(e.target.checked)}
            />
          </div>

          {showScaledDataTable && scaledData.length === 0 && (
            <Alert variant="info" className="mt-3">
              <i className="fas fa-info-circle me-2"></i>
              No scaled data available. Please ensure you have completed Steps 1 and 2 before proceeding with scaling.
            </Alert>
          )}

          {showScaledDataTable && scaledData.length > 0 && (
            <Card className="mt-3">
              <Card.Header>
                <h6 className="mb-0">📊 Scaled KPI Data</h6>
                <small className="text-muted">Original and scaled values for all runs</small>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                      <tr>
                        <th>Run ID</th>
                        {mappedColumns.kpi.map(kpi => (
                          <React.Fragment key={kpi.name}>
                            <th>{kpi.name}</th>
                            <th>{kpi.name} (Scaled)</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {scaledData.map((row, index) => (
                        <tr key={index}>
                          <td><Badge bg="primary">{row.Run_ID}</Badge></td>
                          {mappedColumns.kpi.map(kpi => (
                            <React.Fragment key={kpi.name}>
                              <td>{row[kpi.name] !== undefined && row[kpi.name] !== null ? parseFloat(row[kpi.name]).toFixed(3) : 'N/A'}</td>
                              <td className="text-info fw-bold">{row[`${kpi.name}_scaled`] || 'N/A'}</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="mt-3">
                  <small className="text-muted">
                    📊 Showing {scaledData.length} runs with {mappedColumns.kpi.length} KPIs scaled using {selectedReferenceRunIds.size > 0 ? selectedReferenceRunIds.size : currentReferenceRuns.length} reference runs
                  </small>
                </div>
              </Card.Body>
            </Card>
          )}

          {showDebugTable && (
            <Card className="mt-3">
              <Card.Header>
                <h6 className="mb-0">🔍 Debug: KPI Mapping Flow</h6>
                <small className="text-muted">
                  Step 1 KPI Parameters → Step 3 Scaling
                </small>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <h6>Step 1 - KPI Parameters</h6>
                    <div className="border rounded p-2 mb-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {kpiParameters.length === 0 ? (
                        <small className="text-muted">No KPI parameters selected</small>
                      ) : (
                        kpiParameters.map((param, index) => (
                          <div key={index} className="mb-1">
                            <Badge bg="info" className="me-1">{param.name}</Badge>
                            {/* Removed param.type display as requested */}
                          </div>
                        ))
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <h6>Step 3 - Mapped KPI Columns</h6>
                    <div className="border rounded p-2 mb-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {mappedColumns.kpi.length === 0 ? (
                        <small className="text-muted">No KPI columns mapped</small>
                      ) : (
                        mappedColumns.kpi.map((kpi, index) => (
                          <div key={index} className="mb-1">
                            <Badge bg="success" className="me-1">{kpi.name}</Badge>
                            <small className="text-muted">({kpi.type})</small>
                          </div>
                        ))
                      )}
                    </div>
                  </Col>
                </Row>
                <Alert variant={kpiParameters.length === mappedColumns.kpi.length ? 'success' : 'warning'} className="mt-2">
                  <strong>Sync Status:</strong> {kpiParameters.length} Step 1 parameters → {mappedColumns.kpi.length} Step 3 mapped columns
                  {kpiParameters.length === mappedColumns.kpi.length ? ' ✅' : ' ⚠️'}
                </Alert>
              </Card.Body>
            </Card>
          )}

          {showDebugTable && (
            <Card className="mt-3">
              <Card.Header>
                <h6 className="mb-0">🔍 Debug: Median and MAD Values</h6>
                <small className="text-muted">
                  Computed from {selectedReferenceRunIds.size > 0 ? selectedReferenceRunIds.size : currentReferenceRuns.length} reference runs
                </small>
              </Card.Header>
              <Card.Body>
                {medianMadData.length === 0 && (
                  <Alert variant="warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    No scaling data available. Please complete Steps 1 and 2 first, then click "Process Step 3" to calculate scaling values.
                  </Alert>
                )}
                {medianMadData.length > 0 && (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>KPI</th>
                          <th>Median</th>
                          <th>MAD</th>
                          <th>Samples</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {medianMadData.map((item, index) => (
                          <tr key={index} className={item.samples === 0 ? 'table-warning' : ''}>
                            <td><Badge bg="success">{item.kpi}</Badge></td>
                            <td>
                              <span className="fw-bold">{item.median.toFixed(3)}</span>
                            </td>
                            <td>
                              <span className="fw-bold">{item.mad.toFixed(3)}</span>
                            </td>
                            <td>
                              <Badge bg={item.samples === 0 ? 'danger' : item.samples < 3 ? 'warning' : 'success'}>
                                {item.samples}
                              </Badge>
                            </td>
                            <td>
                              {item.samples === 0 ? (
                                <Badge bg="danger">No Data</Badge>
                              ) : item.samples < 3 ? (
                                <Badge bg="warning">Low Sample</Badge>
                              ) : (
                                <Badge bg="success">Good</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                {medianMadData.some(item => item.samples === 0) && (
                  <Alert variant="warning" className="mt-3">
                    <strong>⚠️ Low Sample Count Detected:</strong>
                    <ul className="mb-0 mt-2">
                      {medianMadData.filter(item => item.samples === 0).map(item => (
                        <li key={item.kpi}>
                          <strong>{item.kpi}:</strong> No valid data found in reference runs (using default values)
                        </li>
                      ))}
                    </ul>
                    <hr />
                    <strong>💡 Recommendation:</strong>
                    <ul className="mb-0">
                      <li>Verify KPI column names match the data exactly</li>
                      <li>Check if reference runs have valid numeric values for these KPIs</li>
                      <li>Consider selecting different reference runs with complete data</li>
                      <li>KPIs with 0 samples will use default scaling values (median=0, mad=0.001)</li>
                    </ul>
                  </Alert>
                )}
                {medianMadData.some(item => item.samples > 0 && item.samples < 3) && (
                  <Alert variant="info" className="mt-3">
                    <strong>ℹ️ Low Sample Count Warning:</strong>
                    <ul className="mb-0 mt-2">
                      {medianMadData.filter(item => item.samples > 0 && item.samples < 3).map(item => (
                        <li key={item.kpi}>
                          <strong>{item.kpi}:</strong> Only {item.samples} sample{item.samples === 1 ? '' : 's'} available
                        </li>
                      ))}
                    </ul>
                    <hr />
                    <strong>💡 Recommendation:</strong>
                    <ul className="mb-0">
                      <li>Consider selecting more reference runs for better statistics</li>
                      <li>Statistical calculations are more reliable with 3+ samples</li>
                    </ul>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 4: Reference Profile
  const Step4ReferenceProfile = () => (
    <Container>
      <Card>
        <Card.Header>
          <h5 className="mb-0">📈 Reference Profile Generation</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="success">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <i className="fas fa-check-circle" style={{ fontSize: '2rem', color: 'green' }}></i>
              </div>
              <div>
                <h6 className="mb-1">Reference Profile Generated</h6>
                <p className="mb-0">
                  Reference profile has been built using the selected reference runs from Step 2. 
                  DTW alignment and median aggregation have been applied to create a robust reference profile.
                </p>
              </div>
            </div>
          </Alert>

          <div className="mt-3">
            <h6>Configuration Summary:</h6>
            <ul className="list-unstyled">
              <li>✅ <strong>Reference Runs:</strong> {selectedReferenceRunIds.size > 0 ? selectedReferenceRunIds.size : currentReferenceRuns.length} selected</li>
              <li>✅ <strong>Scaled KPIs:</strong> {kpiParameters.length} KPI columns processed</li>
              <li>✅ <strong>Alignment Method:</strong> Dynamic Time Warping (DTW)</li>
              <li>✅ <strong>Aggregation:</strong> Median across aligned runs</li>
            </ul>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 5: KPI Weighting
  const Step5KpiWeighting = () => (
    <Container>
      <Card>
        <Card.Header>
          <h5 className="mb-0">⚖️ Hybrid KPI Weighting</h5>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Alert variant="info">
                <strong>Blending Results:</strong>
                <div className="mt-2">
                  <Badge bg="primary" className="me-2">α = {blendingResults.alpha}</Badge>
                  <Badge bg="success">AUROC = {blendingResults.auroc}</Badge>
                </div>
              </Alert>
            </Col>
            <Col md={6}>
              <Alert variant="secondary">
                <small>
                  <strong>Method:</strong> Combines linear (FDR) and tree-based (Gradient Boosting) 
                  weights using optimal α blend ratio based on cross-validation AUROC.
                </small>
              </Alert>
            </Col>
          </Row>

          <Card>
            <Card.Header>
              <h6 className="mb-0">KPI Weight Comparison</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '400px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weightingData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="kpi" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name, props) => [
                        value,
                        name,
                        `α = ${props.payload.alpha}, AUROC = ${blendingResults.auroc}`
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="linearWeight" fill="#8884d8" name="Linear Weight (FDR)" />
                    <Bar dataKey="treeWeight" fill="#82ca9d" name="Tree Weight (GB)" />
                    <Bar dataKey="finalWeight" fill="#ffc658" name="Final Blended Weight" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>

          <Card className="mt-3">
            <Card.Header>
              <h6 className="mb-0">Weight Details</h6>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>KPI</th>
                      <th>Linear Weight</th>
                      <th>Tree Weight</th>
                      <th>Final Weight</th>
                      <th>Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weightingData.map((item, index) => (
                      <tr key={index}>
                        <td><Badge bg="success">{item.kpi}</Badge></td>
                        <td>{item.linearWeight}</td>
                        <td>{item.treeWeight}</td>
                        <td><strong>{item.finalWeight}</strong></td>
                        <td>
                          <ProgressBar 
                            now={parseFloat(item.finalWeight) * 100} 
                            style={{ height: '8px' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 6: Adaptive Anomaly Threshold (EWMA)
  const Step6AnomalyDetection = () => (
    <Container>
      <Card>
        <Card.Header>
          <h5 className="mb-0">🚨 Adaptive Anomaly Detection (EWMA)</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={8}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Anomaly Detection Chart</h6>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={anomalyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="rawScore" stroke="#2196F3" strokeWidth={2} 
                            dot={{ fill: '#2196F3', r: 4 }} name="Raw Score" />
                      <Line type="monotone" dataKey="ewma" stroke="#000000" strokeWidth={2} 
                            name="EWMA" />
                      <Line type="monotone" dataKey="upperBound" stroke="#9E9E9E" strokeDasharray="5 5" 
                            name="3σ Upper" />
                      <Line type="monotone" dataKey="lowerBound" stroke="#9E9E9E" strokeDasharray="5 5" 
                            name="3σ Lower" />
                    </LineChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">EWMA Parameters</h6>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Smoothing Factor (λ): {ewmaLambda}</Form.Label>
                    <Form.Range
                      min={0.1}
                      max={0.9}
                      step={0.1}
                      value={ewmaLambda}
                      onChange={(e) => setEwmaLambda(parseFloat(e.target.value))}
                    />
                    <small className="text-muted">Lower values = more smoothing</small>
                  </Form.Group>
                  
                  <Alert variant="info">
                    <strong>Alarms Detected: {alarmList.length}</strong>
                    <ListGroup className="mt-2">
                      {alarmList.slice(0, 5).map((alarm, index) => (
                        <ListGroup.Item key={index} className="py-1">
                          <Badge bg={alarm.severity === 'HIGH' ? 'danger' : 'warning'} className="me-2">
                            {alarm.runId}
                          </Badge>
                          Day {alarm.day}
                        </ListGroup.Item>
                      ))}
                      {alarmList.length > 5 && (
                        <ListGroup.Item className="text-center py-1">
                          <small>+{alarmList.length - 5} more alarms</small>
                        </ListGroup.Item>
                      )}
                    </ListGroup>
                  </Alert>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 7: Event Logging with Severity
  const Step7EventLogging = () => (
    <Container>
      <Card>
        <Card.Header>
          <h5 className="mb-0">📝 Event Logging with Severity</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={8}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Event Log</h6>
                  <div>
                    <Form.Control 
                      type="text" 
                      placeholder="Filter by Run, Variable..." 
                      size="sm"
                      style={{ width: '200px' }}
                      value={eventFilters.run}
                      onChange={(e) => setEventFilters({...eventFilters, run: e.target.value})}
                    />
                  </div>
                </Card.Header>
                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Run</th>
                        <th>Day</th>
                        <th>Variable</th>
                        <th>Severity %</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventLogData.map((event) => (
                        <tr key={event.id}>
                          <td>{event.run}</td>
                          <td>{event.day}</td>
                          <td>{event.variable}</td>
                          <td>
                            <Badge bg={event.severityColor}>
                              {event.severity}%
                            </Badge>
                          </td>
                          <td>{event.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Instrument Floors (JSON Editor)</h6>
                </Card.Header>
                <Card.Body>
                  <Form.Group>
                    <Form.Label>Edit Detection Thresholds:</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={8}
                      value={JSON.stringify(instrumentFloors, null, 2)}
                      onChange={(e) => {
                        try {
                          setInstrumentFloors(JSON.parse(e.target.value));
                        } catch (error) {
                          // Invalid JSON, ignore for now
                        }
                      }}
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                  </Form.Group>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => {
                      const csv = eventLogData.map(e => 
                        `${e.run},${e.day},${e.variable},${e.severity},${e.value}`
                      ).join('\n');
                      console.log('Event Log CSV:', csv);
                      alert('Event log exported to console (CSV format)');
                    }}
                  >
                    📁 Export CSV
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 8: KPI-Event Lift Matrix
  const Step8LiftMatrix = () => (
    <Container>
      <Card>
        <Card.Header>
          <h5 className="mb-0">🔥 KPI-Event Lift Matrix</h5>
        </Card.Header>
        <Card.Body>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Lift Heat Map (Click cells to analyze time-lag)</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ overflowX: 'auto' }}>
                <Table bordered className="lift-matrix-table">
                  <thead>
                    <tr>
                      <th>KPI / Input</th>
                      {mappedColumns.input.map(input => (
                        <th key={input.id} className="text-center">{input.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {liftMatrix.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td><strong>{row.kpi}</strong></td>
                        {mappedColumns.input.map(input => {
                          const cellData = row[input.name];
                          const liftValue = parseFloat(cellData?.lift || 0);
                          const bgColor = liftValue > 2 ? '#ff4444' : liftValue > 1.5 ? '#ffaa44' : '#44ff44';
                          
                          return (
                            <td 
                              key={input.id} 
                              className="text-center"
                              style={{ 
                                backgroundColor: bgColor + '40',
                                cursor: 'pointer',
                                position: 'relative'
                              }}
                              onClick={() => {
                                setSelectedInput(input.name);
                                setSelectedKpi(row.kpi);
                                setSelectedMatrixCell({kpi: row.kpi, input: input.name});
                                alert(`Selected ${input.name} → ${row.kpi} for time-lag analysis. Go to Step 9!`);
                              }}
                              title={`Lift: ${cellData?.lift}, Count: ${cellData?.count}, Examples: ${cellData?.examples?.join(', ')}`}
                            >
                              <strong>{cellData?.lift}</strong>
                              <br />
                              <small>({cellData?.count})</small>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              {selectedMatrixCell && (
                <Alert variant="info" className="mt-3">
                  <strong>Selected:</strong> {selectedMatrixCell.input} → {selectedMatrixCell.kpi}
                  <Button variant="link" onClick={() => setSelectedMatrixCell(null)} className="float-end p-0">×</Button>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 9: Time-Lag Analysis
  const Step9TimeLagAnalysis = () => (
    <Container>
      <Card>
        <Card.Header>
          <h5 className="mb-0">⏰ Time-Lag Analysis</h5>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Input Variable</Form.Label>
                <Form.Select 
                  value={selectedInput} 
                  onChange={(e) => setSelectedInput(e.target.value)}
                >
                  <option value="">Select input...</option>
                  {mappedColumns.input.map(input => (
                    <option key={input.id} value={input.name}>{input.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>KPI Variable</Form.Label>
                <Form.Select 
                  value={selectedKpi} 
                  onChange={(e) => setSelectedKpi(e.target.value)}
                >
                  <option value="">Select KPI...</option>
                  {mappedColumns.kpi.map(kpi => (
                    <option key={kpi.id} value={kpi.name}>{kpi.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Max Lag (days): {maxLag}</Form.Label>
                <Form.Range
                  min={1}
                  max={7}
                  value={maxLag}
                  onChange={(e) => setMaxLag(parseInt(e.target.value))}
                />
              </Form.Group>
            </Col>
          </Row>

          {selectedInput && selectedKpi && (
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Lag vs Correlation</h6>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={lagAnalysisData.correlation}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="lag" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" stroke="#2196F3" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Lag vs P-Value</h6>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={lagAnalysisData.pvalue}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="lag" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" stroke="#FF9800" strokeWidth={2} />
                        <Line type="monotone" dataKey={() => 0.05} stroke="#F44336" strokeDasharray="5 5" name="p=0.05" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {!selectedInput || !selectedKpi ? (
            <Alert variant="warning">
              Please select both an Input variable and a KPI to perform time-lag analysis.
            </Alert>
          ) : (
            <Button 
              variant="primary" 
              className="mt-3"
              onClick={() => {
                processTimeLagAnalysis();
                alert('Time-lag analysis updated with current parameters!');
              }}
            >
              🔄 Run Analysis
            </Button>
          )}
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 10: Composite Root-Cause Scoring
  const Step10RootCauseScoring = () => (
    <Container>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">🎯 Composite Root-Cause Scoring</h5>
          <div>
            <Button 
              variant="outline-success" 
              size="sm" 
              className="me-2"
              onClick={() => {
                const csv = rootCauseData.map(r => 
                  `${r.kpi},${r.input},${r.deviation},${r.correlation},${r.pvalue},${r.lift},${r.compositeScore},${r.suggestedAction}`
                ).join('\n');
                console.log('Root Cause CSV:', csv);
                alert('Root cause data exported to console');
              }}
            >
              📁 Export CSV
            </Button>
            <Button 
              variant="outline-danger" 
              size="sm"
              onClick={() => alert('PDF export would be implemented here')}
            >
              📄 Export PDF
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>KPI</th>
                  <th>Input</th>
                  <th>Deviation (MADs)</th>
                  <th>ρ (correlation)</th>
                  <th>p-value</th>
                  <th>Lift</th>
                  <th>Composite Score</th>
                  <th>Suggested Action</th>
                </tr>
              </thead>
              <tbody>
                {rootCauseData.map((cause, index) => (
                  <tr key={cause.id}>
                    <td>
                      <Badge bg={index < 3 ? 'danger' : index < 7 ? 'warning' : 'secondary'}>
                        #{index + 1}
                      </Badge>
                    </td>
                    <td><Badge bg="info">{cause.kpi}</Badge></td>
                    <td><Badge bg="secondary">{cause.input}</Badge></td>
                    <td>{cause.deviation}</td>
                    <td>{cause.correlation}</td>
                    <td style={{ color: parseFloat(cause.pvalue) < 0.05 ? 'green' : 'red' }}>
                      {cause.pvalue}
                    </td>
                    <td>{cause.lift}</td>
                    <td>
                      <ProgressBar 
                        now={parseFloat(cause.compositeScore)} 
                        label={cause.compositeScore}
                        style={{ minWidth: '80px' }}
                        variant={parseFloat(cause.compositeScore) > 80 ? 'danger' : 
                               parseFloat(cause.compositeScore) > 60 ? 'warning' : 'success'}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={editableActions[cause.id] || cause.suggestedAction}
                        onChange={(e) => setEditableActions({
                          ...editableActions,
                          [cause.id]: e.target.value
                        })}
                        style={{ minWidth: '200px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          
          <Alert variant="success" className="mt-3">
            <strong>Analysis Complete!</strong> 
            {rootCauseData.length} root causes identified and ranked by composite score.
            Top causes should be investigated first.
          </Alert>
        </Card.Body>
      </Card>
    </Container>
  );

  // Step 11: Lifecycle & Traceability

  const Step11LifecycleTraceability = () => {
    // Filter audit history based on search
    const filteredHistory = auditHistory.filter(record => 
      record.runId.toLowerCase().includes(auditFilter.toLowerCase()) ||
      record.date.includes(auditFilter) ||
      record.status.toLowerCase().includes(auditFilter.toLowerCase())
    );

    return (
      <Container>
        <Row>
          <Col md={12}>
            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">📋 Lifecycle & Traceability</h5>
                <div>
                  <Badge bg="success" className="me-2">CFR 21 Part 11 Compliant</Badge>
                  <Badge bg="info" className="me-2">GMP Validated</Badge>
                  <Badge bg="secondary">Data Integrity Verified</Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <Alert variant="info">
                  <strong>Regulatory Compliance:</strong> This analysis maintains full audit trail compliance 
                  with FDA CFR 21 Part 11, GMP guidelines, and data integrity requirements.
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">📄 Current Configuration</h6>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShowConfigModal(true)}
                >
                  👁️ View Full Config
                </Button>
              </Card.Header>
              <Card.Body>
                {auditConfig ? (
                  <div>
                    <Row>
                      <Col sm={6}><strong>Run ID:</strong></Col>
                      <Col sm={6}><code>{auditConfig.run_id}</code></Col>
                    </Row>
                    <Row>
                      <Col sm={6}><strong>Date:</strong></Col>
                      <Col sm={6}>{auditConfig.run_date}</Col>
                    </Row>
                    <Row>
                      <Col sm={6}><strong>AUROC:</strong></Col>
                      <Col sm={6}><Badge bg="success">{auditConfig.auroc}</Badge></Col>
                    </Row>
                    <Row>
                      <Col sm={6}><strong>Distance Metric:</strong></Col>
                      <Col sm={6}><Badge bg="info">{auditConfig.distance_metric}</Badge></Col>
                    </Row>
                    <Row>
                      <Col sm={6}><strong>Reference Runs:</strong></Col>
                      <Col sm={6}>{auditConfig.reference_runs.length} selected</Col>
                    </Row>
                    <Row>
                      <Col sm={6}><strong>Pipeline Version:</strong></Col>
                      <Col sm={6}><Badge bg="secondary">{auditConfig.pipeline_version}</Badge></Col>
                    </Row>
                  </div>
                ) : (
                  <Alert variant="warning">
                    No audit configuration generated yet. Process the lifecycle step to create audit trail.
                  </Alert>
                )}
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>
                <h6 className="mb-0">📦 Download Options</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button 
                    variant="primary" 
                    onClick={downloadAuditBundle}
                    disabled={!auditConfig}
                  >
                    📁 Download Audit Bundle (YAML + CSVs)
                  </Button>
                  <Button 
                    variant="outline-secondary"
                    onClick={() => alert('Archive download would connect to backend storage')}
                  >
                    🗄️ Download Archive (Last 90 days)
                  </Button>
                </div>
                <small className="text-muted mt-2 d-block">
                  <strong>Archive Policy:</strong> Analysis records are automatically archived after 90 days. 
                  Archived data is available for 7 years for regulatory compliance.
                </small>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">🕐 Audit History (Last 25 Runs)</h6>
                <Form.Control
                  type="text"
                  placeholder="Filter by run ID, date, or status..."
                  value={auditFilter}
                  onChange={(e) => setAuditFilter(e.target.value)}
                  style={{ width: '300px' }}
                  size="sm"
                />
              </Card.Header>
              <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredHistory.length > 0 ? (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Run ID</th>
                        <th>AUROC</th>
                        <th>Metric</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((record) => (
                        <tr key={record.id}>
                          <td>{record.date}</td>
                          <td>
                            <code style={{ fontSize: '0.75em' }}>
                              {record.runId}
                            </code>
                          </td>
                          <td>
                            <Badge bg={parseFloat(record.auroc) > 0.9 ? 'success' : 'warning'}>
                              {record.auroc}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="info" className="text-capitalize">
                              {record.distanceMetric}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={record.status === 'current' ? 'success' : 'secondary'}>
                              {record.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Alert variant="warning">
                    No audit records match the current filter.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Configuration Modal */}
        <Modal 
          show={showConfigModal} 
          onHide={() => setShowConfigModal(false)} 
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>📄 Full Audit Configuration</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {auditConfig ? (
              <pre style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '5px',
                fontSize: '0.85em',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
{`# RapidCMC KPI Analysis - Audit Configuration
run_date: ${auditConfig.run_date}
run_id: ${auditConfig.run_id}
project_id: ${auditConfig.project_id}
worksheet_name: ${auditConfig.worksheet_name}

# Analysis Configuration
reference_runs: [${auditConfig.reference_runs.join(', ')}]
weight_alpha: ${auditConfig.weight_alpha}
distance_metric: ${auditConfig.distance_metric}
ewma_lambda: ${auditConfig.ewma_lambda}
auroc: ${auditConfig.auroc}
threshold_rule: "${auditConfig.threshold_rule}"

# Data Mappings
kpi_columns: [${auditConfig.kpi_columns.join(', ')}]
input_columns: [${auditConfig.input_columns.join(', ')}]

# Analysis Results
good_runs_count: ${auditConfig.good_runs_count}
bad_runs_count: ${auditConfig.bad_runs_count}
anomalies_detected: ${auditConfig.anomalies_detected}
events_logged: ${auditConfig.events_logged}
root_causes_identified: ${auditConfig.root_causes_identified}

# Compliance
regulatory_compliance:
  cfr_21_part11: ${auditConfig.regulatory_compliance.cfr_21_part11}
  gmp_compliant: ${auditConfig.regulatory_compliance.gmp_compliant}
  data_integrity: "${auditConfig.regulatory_compliance.data_integrity}"

# System Information
pipeline_version: ${auditConfig.pipeline_version}
generated_timestamp: ${new Date().toISOString()}`}
              </pre>
            ) : (
              <Alert variant="warning">No configuration available</Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
              Close
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                downloadAuditBundle();
                setShowConfigModal(false);
              }}
              disabled={!auditConfig}
            >
              📁 Download YAML
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    );
  };

  // Main render function
  return (
    <Modal show={show} onHide={onHide} size="xl" className="kpi-calculation-modal">
      <Modal.Header closeButton>
        <Modal.Title>📈 Calculate KPI - Multi-Step Wizard</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <StepIndicator />
        
        <div className="step-content">
          {currentStep === 1 && <Step1ColumnMapping />}
          {currentStep === 2 && <Step2GoodBadSplit />}
          {currentStep === 3 && <Step3RobustScaling />}
          {currentStep === 4 && <Step4ReferenceProfile />}
          {currentStep === 5 && <Step5KpiWeighting />}
          {currentStep === 6 && <Step6AnomalyDetection />}
          {currentStep === 7 && <Step7EventLogging />}
          {currentStep === 8 && <Step8LiftMatrix />}
          {currentStep === 9 && <Step9TimeLagAnalysis />}
          {currentStep === 10 && <Step10RootCauseScoring />}
          {currentStep === 11 && <Step11LifecycleTraceability />}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <small className="text-muted">
              Step {currentStep} of {steps.length}
              {currentStep === 1 && ` - Input: ${inputParameters.length}, KPI: ${kpiParameters.length} parameters`}
              {currentStep === 2 && ` - ${availableGoodRuns.length} good runs available${selectedReferenceRunIds.size > 0 ? `, ${selectedReferenceRunIds.size} selected as reference` : ''}`}
              {currentStep === 3 && ` - ${kpiParameters.length} KPIs scaled using reference runs`}
              {currentStep === 4 && ` - Reference profile built using ${selectedReferenceRunIds.size > 0 ? selectedReferenceRunIds.size : currentReferenceRuns.length} reference runs`}
              {currentStep === 5 && ` - ${weightingData.length} KPI weights computed`}
              {currentStep === 6 && ` - ${alarmList.length} anomalies detected`}
              {currentStep === 7 && ` - ${eventLogData.length} events logged`}
              {currentStep === 8 && ` - ${liftMatrix.length} lift pairs analyzed`}
              {currentStep === 9 && selectedInput && selectedKpi && ` - Time-lag analysis: ${selectedInput} → ${selectedKpi}`}
              {currentStep === 10 && ` - ${rootCauseData.length} root causes scored`}
              {currentStep === 11 && auditConfig && ` - Audit trail: ${auditHistory.length} history records`}
            </small>
          </div>
          
          <div>
            <Button 
              variant="secondary" 
              onClick={onHide} 
              className="me-2"
            >
              Cancel
            </Button>
            
            {currentStep > 1 && (
              <Button 
                variant="outline-primary" 
                onClick={prevStep}
                className="me-2"
              >
                ← Back
              </Button>
            )}
            
            {currentStep < 11 ? (
              <Button 
                variant="primary" 
                onClick={nextStep}
                disabled={!canProceedToNextStep()}
              >
                Next →
              </Button>
            ) : (
              <Button 
                variant="success" 
                onClick={handleFinish}
              >
                🎯 Complete Analysis
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default KpiCalculationModal;
